import { v4 as uuid } from "uuid";
import { metadataService } from "./metadataService";
import type { SemanticMetadata, SemanticType, ValidationIssue } from "../types/SemanticTypes";
import { applyTypographyToRange, applyTypographyToRangeDeep, bodyFont, labelForHeading, ONE_HALF_LINE, SINGLE_LINE, specForMetadata, typographyByType } from "./typographyService";
import type { TypographySpec } from "./typographyService";
import { normalizeOrcid, normalizeRor, validateEmail, ORCID_ICON_BASE64, ROR_ICON_BASE64 } from "./orcid";
import type { AuthorInput } from "./orcid";

interface PreparedAuthor {
  name: string;
  affiliation: string;
  emailAddress: string;
  emailUrl?: string;
  orcidUrl?: string;
  rorUrl?: string;
  roles?: string[];
  ojsContributorId?: number;
}
import { splitNumberedReferences, stripLeadingMarker, toTitleCase } from "./textCleanup";
import { ommlToHtml } from "./omml";

export interface ApplySemanticOptions {
  type: SemanticType;
  styleBuiltIn?: Word.BuiltInStyleName | string;
  metadata?: Partial<SemanticMetadata>;
  requireTableSelection?: boolean;
  // Override the content-control title. Used by auto-tag so a detected
  // Abstract/Keywords/References heading gets the same "Libertatem <Label>
  // Heading" title the manual insert flow uses — the exporter strips those
  // from the body so the frontmatter heading isn't duplicated below the panel.
  controlTitle?: string;
}

export class WordService {
  private readonly batchableParagraphTypes = new Set<SemanticType>([
    "section",
    "paragraph",
    "figure",
    "figure_caption",
    "table_caption",
    "table_note",
    "equation",
    "blockquote",
    "reference"
  ]);

  async applySemanticTag(options: ApplySemanticOptions): Promise<SemanticMetadata> {
    return Word.run(async (context) => {
      const metadata = metadataService.create(options.type, options.metadata);
      const spec = specForMetadata(metadata);
      if (!spec.preserveRuns) this.applyDocumentDefaults(context);
      const selection = context.document.getSelection();
      selection.load("text");
      const paragraphs = selection.paragraphs;
      paragraphs.load("items,text");
      const tables = selection.tables;
      tables.load("items");
      const pictures = selection.inlinePictures;
      pictures.load("items");
      await context.sync();

      if (options.requireTableSelection && tables.items.length === 0) {
        throw new Error("Select a native Word table before applying this tag.");
      }

      // Re-tag in place: if the selection (or its paragraph) is already inside a
      // Libertatem content control, update that control's tag/title/typography
      // instead of nesting a new one. This lets the user click tagged content and
      // press a different tag button to change it (e.g., H1 → H2) without
      // untagging first.
      const existingControl = selection.parentContentControlOrNullObject;
      existingControl.load("isNullObject,tag,id");
      await context.sync();
      if (!existingControl.isNullObject) {
        const existingMeta = metadataService.decode(existingControl.tag);
        if (existingMeta) {
          const updatedMeta = metadataService.create(options.type, {
            ...options.metadata,
            id: existingMeta.id,
            createdAt: existingMeta.createdAt
          });
          existingControl.tag = metadataService.encode(updatedMeta);
          existingControl.title = this.titleForMetadata(updatedMeta);
          if (!spec.preserveRuns) {
            const controlRange = existingControl.getRange("Content");
            await applyTypographyToRangeDeep(context, controlRange, spec);
          }
          await metadataService.upsertDocumentIndex(context, updatedMeta);
          await context.sync();
          return updatedMeta;
        }
      }

      // A Figure is always applied to an existing image, never to empty text.
      // The image (inline OR floating/anchored) reports empty selection text, and
      // floating images don't appear in `selection.inlinePictures` — so the only
      // safe rule is: for a figure, NEVER insert a "Type text here" prompt (that
      // replaces and deletes the image). Fall straight through to the wrap path.
      const isImageSelection = pictures.items.length > 0 || options.type === "figure";
      if (selection.text.trim().length === 0 && !isImageSelection) {
        let prompt = "Type text here";
        if (options.type === "title") prompt = "Type title here";
        else if (options.type === "subtitle") prompt = "Type subtitle here";
        else if (options.type === "author") prompt = "Type author name and affiliation here";
        else if (options.type === "section") prompt = `Type heading ${options.metadata?.level ?? 1} here`;
        else if (options.type === "paragraph") prompt = "Type paragraph here";
        else if (options.type === "blockquote") prompt = "Type blockquote here";
        else if (options.type === "reference") prompt = "Type reference here";
        else if (options.type === "equation") prompt = "Type equation here";
        else if (options.type === "figure_caption") prompt = "Type figure caption here";
        else if (options.type === "table_caption") prompt = "Type table caption here";
        else if (options.type === "table_note") prompt = "Type table note here";
        else if (options.type === "abstract") prompt = "Type abstract body here";
        else if (options.type === "keywords") prompt = "Type comma-separated keywords here";
        else if (options.type === "footnote") prompt = "Type footnote here";

        const range = selection.insertText(prompt, Word.InsertLocation.replace);
        await applyTypographyToRangeDeep(context, range, spec);
        const control = range.insertContentControl();
        control.title = this.titleForMetadata(metadata);
        control.tag = metadataService.encode(metadata);
        control.appearance = Word.ContentControlAppearance.boundingBox;

        if (options.type === "title" || options.type === "subtitle" || options.type === "author") {
          const after = range.insertParagraph("", Word.InsertLocation.after);
          after.font.name = bodyFont();
          after.font.size = 11;
          after.alignment = "Justified";
          after.lineSpacing = 12;
        }

        range.select("Select");
        await metadataService.upsertDocumentIndex(context, metadata);
        await context.sync();
        return metadata;
      }

      if (options.requireTableSelection && tables.items.length > 1) {
        const metadataList = tables.items.map((table) => this.queueSemanticTagOnRange(table.getRange(), options));
        await metadataService.upsertDocumentIndexMany(context, metadataList);
        await context.sync();
        return metadataList[metadataList.length - 1];
      }

      if (this.batchableParagraphTypes.has(options.type) && paragraphs.items.length > 1) {
        const metadataList: SemanticMetadata[] = [];
        for (const paragraph of paragraphs.items) {
          const text = paragraph.text.trim();
          if (!text) continue;
          const targetRange = options.type === "paragraph" ? paragraph.getRange("Content") : paragraph.getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(targetRange, options, text));
        }
        if (metadataList.length === 0) throw new Error("Select at least one non-empty paragraph.");
        await metadataService.upsertDocumentIndexMany(context, metadataList);
        await context.sync();
        return metadataList[metadataList.length - 1];
      }

      if (this.batchableParagraphTypes.has(options.type) && paragraphs.items.length === 1) {
        const onlyParagraph = paragraphs.items[0];
        const text = onlyParagraph.text.trim();
        if (text) {
          const targetRange = options.type === "paragraph" ? onlyParagraph.getRange("Content") : onlyParagraph.getRange("Whole");
          const tagged = this.queueSemanticTagOnRange(targetRange, options, text);
          await metadataService.upsertDocumentIndex(context, tagged);
          await context.sync();
          return tagged;
        }
      }

      // Headings: strip any leading numbering/bullet the author typed
      // (e.g. "1. Introduction:" -> "Introduction:"). We renumber/structure
      // semantically, so the manual marker is noise.
      if (options.type === "section") {
        const stripped = stripLeadingMarker(selection.text);
        if (stripped && stripped !== selection.text) {
          selection.insertText(stripped, Word.InsertLocation.replace);
        }
      }

      // Title and subtitle: convert to title case
      if (options.type === "title" || options.type === "subtitle") {
        const titleCased = toTitleCase(selection.text);
        if (titleCased && titleCased !== selection.text) {
          selection.insertText(titleCased, Word.InsertLocation.replace);
        }
      }

      if (options.styleBuiltIn) {
        selection.styleBuiltIn = options.styleBuiltIn as Word.BuiltInStyleName;
      }
      await applyTypographyToRangeDeep(context, selection, spec);
      const control = selection.insertContentControl();
      control.title = this.titleForMetadata(metadata);
      control.tag = metadataService.encode(metadata);
      control.appearance = Word.ContentControlAppearance.boundingBox;
      await metadataService.upsertDocumentIndex(context, metadata);
      await context.sync();
      return metadata;
    });
  }

  private queueSemanticTagOnRange(range: Word.Range, options: ApplySemanticOptions, currentText?: string): SemanticMetadata {
    const metadata = metadataService.create(options.type, options.metadata);
    const spec = specForMetadata(metadata);
    if (options.type === "section" && currentText) {
      const stripped = stripLeadingMarker(currentText);
      if (stripped && stripped !== currentText) {
        range.insertText(stripped, Word.InsertLocation.replace);
      }
    }
    if ((options.type === "title" || options.type === "subtitle") && currentText) {
      const titleCased = toTitleCase(currentText);
      if (titleCased && titleCased !== currentText) {
        range.insertText(titleCased, Word.InsertLocation.replace);
      }
    }
    if (!spec.preserveRuns && options.styleBuiltIn) {
      range.styleBuiltIn = options.styleBuiltIn as Word.BuiltInStyleName;
    }
    if (!spec.preserveRuns) applyTypographyToRange(range, spec);
    const control = range.insertContentControl();
    control.title = options.controlTitle ?? this.titleForMetadata(metadata);
    control.tag = metadataService.encode(metadata);
    control.appearance = Word.ContentControlAppearance.boundingBox;
    return metadata;
  }

  // Insert a stacked author block at the cursor. Each author: name (bold 11pt),
  // affiliation (italic 11pt), optional ORCID icon linked to the profile — all
  // centered. ORCIDs are validated up front so we never touch the document on
  // bad input. Each author block is wrapped in an "author" content control.
  // Validate every author's identifiers up front so we never touch the document
  private prepareAuthors(authors: AuthorInput[]): PreparedAuthor[] {
    const prepared = authors
      .filter((a) => a.name.trim() || a.affiliation.trim() || a.orcid.trim() || a.email.trim() || a.ror.trim() || (a.roles && a.roles.length > 0))
      .map((a) => {
        const who = a.name.trim() || "Author";
        const orcid = normalizeOrcid(a.orcid);
        if (!orcid.ok) throw new Error(`${who}: ${orcid.error}`);
        const ror = normalizeRor(a.ror);
        if (!ror.ok) throw new Error(`${who}: ${ror.error}`);
        const email = validateEmail(a.email);
        if (!email.ok) throw new Error(`${who}: ${email.error}`);
        return {
          name: a.name.trim(),
          affiliation: a.affiliation.trim(),
          emailAddress: a.email.trim(),
          emailUrl: email.url,
          orcidUrl: orcid.url,
          rorUrl: ror.url,
          roles: a.roles,
          ojsContributorId: a.ojsContributorId
        };
      });
    if (prepared.length === 0) throw new Error("Add at least one author with a name.");
    return prepared;
  }

  // Render one author block (name bold, affiliation italic, email, ORCID icon,
  // ROR link — all centered) starting after `anchor`. Caller wraps it in an
  // "author" content control. Returns the first/last paragraphs of the block.
  private renderAuthorBlock(anchor: Word.Range, author: PreparedAuthor): { nameP: Word.Paragraph; last: Word.Paragraph } {
    const nameP = anchor.insertParagraph(author.name, Word.InsertLocation.after);
    nameP.alignment = "Centered";
    nameP.font.name = bodyFont();
    nameP.font.size = 11;
    nameP.font.bold = true;
    nameP.font.italic = false;
    nameP.font.color = "black";
    nameP.lineSpacing = 12;
    let last: Word.Paragraph = nameP;

    if (author.affiliation) {
      const p = last.insertParagraph(author.affiliation, Word.InsertLocation.after);
      p.alignment = "Centered";
      p.font.name = bodyFont();
      p.font.size = 11;
      p.font.italic = true;
      p.font.bold = false;
      p.font.color = "black";
      p.lineSpacing = 12;
      last = p;
    }

    if (author.emailAddress) {
      const p = last.insertParagraph("", Word.InsertLocation.after);
      p.alignment = "Centered";
      p.lineSpacing = 12;
      const t = p.insertText(author.emailAddress, Word.InsertLocation.start);
      t.hyperlink = author.emailUrl ?? `mailto:${author.emailAddress}`;
      t.font.name = bodyFont();
      t.font.size = 11;
      t.font.color = "black";
      last = p;
    }

    if (author.orcidUrl) {
      const p = last.insertParagraph("", Word.InsertLocation.after);
      p.alignment = "Centered";
      p.lineSpacing = 12;
      const picture = p.insertInlinePictureFromBase64(ORCID_ICON_BASE64, Word.InsertLocation.start);
      picture.width = 12;
      picture.height = 12;
      picture.hyperlink = author.orcidUrl;
      picture.altTextTitle = "ORCID iD";
      const t = p.insertText(` ${author.orcidUrl}`, Word.InsertLocation.end);
      t.hyperlink = author.orcidUrl;
      t.font.name = bodyFont();
      t.font.size = 11;
      t.font.color = "black";
      last = p;
    }

    if (author.rorUrl) {
      const p = last.insertParagraph("", Word.InsertLocation.after);
      p.alignment = "Centered";
      p.lineSpacing = 12;
      const picture = p.insertInlinePictureFromBase64(ROR_ICON_BASE64, Word.InsertLocation.start);
      picture.width = 12;
      picture.height = 12;
      picture.hyperlink = author.rorUrl;
      picture.altTextTitle = "ROR ID";
      const t = p.insertText(` ${author.rorUrl}`, Word.InsertLocation.end);
      t.hyperlink = author.rorUrl;
      t.font.name = bodyFont();
      t.font.size = 11;
      t.font.color = "black";
      last = p;
    }

    if (author.roles && author.roles.length > 0) {
      const p = last.insertParagraph(`Roles: ${author.roles.join(", ")}`, Word.InsertLocation.after);
      p.alignment = "Centered";
      p.font.name = bodyFont();
      p.font.size = 11;
      p.font.italic = true;
      p.font.bold = false;
      p.font.color = "black";
      p.lineSpacing = 12;
      last = p;
    }

    return { nameP, last };
  }

  private wrapAuthorBlock(context: Word.RequestContext, nameP: Word.Paragraph, last: Word.Paragraph, ojsContributorId?: number): Promise<void> {
    const meta = metadataService.create("author", ojsContributorId != null ? { ojsContributorId } : {});
    const blockRange = nameP.getRange("Start").expandTo(last.getRange("End"));
    const control = blockRange.insertContentControl();
    control.title = this.titleForMetadata(meta);
    control.tag = metadataService.encode(meta);
    control.appearance = Word.ContentControlAppearance.boundingBox;
    return metadataService.upsertDocumentIndex(context, meta);
  }

  async insertAuthors(authors: AuthorInput[]): Promise<void> {
    const prepared = this.prepareAuthors(authors);

    // Strict placement: if author blocks already exist, replace the entire set
    // in place (never create a second block). Otherwise insert directly below the
    // subtitle, else below the title. NEVER at the cursor.
    if (await this.hasAuthorControls()) {
      await this.updateAuthors(authors);
      return;
    }

    await Word.run(async (context) => {
      this.applyDocumentDefaults(context);
      const anchorBase = await this.authorInsertAnchor(context);
      if (!anchorBase) {
        throw new Error("Tag a Title (or Subtitle) first — author details are placed directly below it.");
      }
      let anchor: Word.Range = anchorBase;
      for (const author of prepared) {
        const { nameP, last } = this.renderAuthorBlock(anchor, author);
        await this.wrapAuthorBlock(context, nameP, last, author.ojsContributorId);
        anchor = last.getRange("End");
      }
      await context.sync();
    });
  }

  private async hasAuthorControls(): Promise<boolean> {
    return Word.run(async (context) => {
      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();
      return controls.items.some((ctrl) => metadataService.decode(ctrl.tag)?.type === "author");
    });
  }

  // Resolve where the first author block lands: after the subtitle, else after
  // the title. Returns null when neither exists (caller errors — we never place
  // authors at the cursor).
  // Mac Word: ctrl.getRange("After"/"End") can land INSIDE the content control.
  // Use the control's last paragraph + insertParagraph to reliably land outside.
  private async authorInsertAnchor(context: Word.RequestContext): Promise<Word.Range | null> {
    const controls = context.document.contentControls;
    controls.load("items/tag");
    await context.sync();

    let subtitle: Word.ContentControl | null = null;
    let title: Word.ContentControl | null = null;
    for (const ctrl of controls.items) {
      const meta = metadataService.decode(ctrl.tag);
      if (!meta) continue;
      if (meta.type === "subtitle") subtitle = ctrl;
      else if (meta.type === "title") title = ctrl;
    }

    const target = subtitle ?? title;
    if (!target) return null;
    const paras = target.paragraphs;
    paras.load("items");
    await context.sync();
    return paras.items[paras.items.length - 1].getRange("After");
  }

  // Read existing author blocks back into editable fields. Parses the rendered
  // block (name, affiliation, email, ORCID, ROR) so it works for blocks inserted
  // before structured data was stored.
  async readAuthors(): Promise<AuthorInput[]> {
    return Word.run(async (context) => {
      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();
      const authorControls = controls.items.filter((c) => metadataService.decode(c.tag)?.type === "author");
      const authorMeta = authorControls.map((c) => metadataService.decode(c.tag));

      // Each author is wrapped in its own content control (name + affiliation +
      // id paragraphs). Group strictly by control — one control = one author.
      // The old line-heuristic mis-split designation lines like
      // "CEO, The Law Brigade Group" (no university/dept keyword) into phantom
      // authors, which then stacked on the cover.
      const paraColls = authorControls.map((c) => c.getRange("Content").paragraphs);
      paraColls.forEach((p) => p.load("items/text"));
      await context.sync();

      const result: AuthorInput[] = [];
      for (const [controlIndex, paragraphs] of paraColls.entries()) {
        const lines = paragraphs.items.map((p) => p.text.trim()).filter(Boolean);
        if (!lines.length) continue;
        const orcid = lines.find((l) => /orcid\.org/i.test(l)) ?? "";
        const email = lines.find((l) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(l)) ?? "";
        const ror = lines.find((l) => /ror\.org/i.test(l)) ?? "";
        const rolesLine = lines.find((l) => /^(contributor\s+)?roles?:/i.test(l)) ?? "";
        const roles = rolesLine ? rolesLine.replace(/^(contributor\s+)?roles?:\s*/i, "").split(",").map((r) => r.trim()).filter(Boolean) : [];
        const rest = lines.filter((l) => l !== orcid && l !== email && l !== ror && l !== rolesLine)
          .map((l) => l.replace(/^by\s+/i, "").trim()).filter(Boolean);
        const ojsContributorId = authorMeta[controlIndex]?.ojsContributorId;
        result.push({ name: rest[0] ?? "", affiliation: rest.slice(1).join(", "), orcid, email, ror, roles, ojsContributorId });
      }
      return result;
    });
  }

  // Replace the document's author blocks with the edited set, in place.
  async updateAuthors(authors: AuthorInput[]): Promise<void> {
    const prepared = this.prepareAuthors(authors);
    await Word.run(async (context) => {
      this.applyDocumentDefaults(context);
      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();
      const authorControls = controls.items.filter((c) => metadataService.decode(c.tag)?.type === "author");
      if (authorControls.length === 0) {
        throw new Error("No authors found in the document. Use Insert authors instead.");
      }

      // Materialize an insertion anchor first, then remove old author blocks,
      // then rebuild after the anchor. Doing all three in one batch let Word
      // swallow the new content with the old controls during deletion.
      const marker = authorControls[0].getRange("Start").insertParagraph("", Word.InsertLocation.before);
      marker.track();
      await context.sync();

      for (const control of authorControls) {
        control.delete(false);
      }
      await context.sync();

      let anchor: Word.Range = marker.getRange("End");
      for (const author of prepared) {
        const { nameP, last } = this.renderAuthorBlock(anchor, author);
        await this.wrapAuthorBlock(context, nameP, last, author.ojsContributorId);
        anchor = last.getRange("End");
      }
      marker.delete();
      marker.untrack();
      await context.sync();
    });
  }

  async insertLabeledSemanticBlock(type: "abstract" | "keywords" | "references"): Promise<SemanticMetadata> {
    return Word.run(async (context) => {
      this.applyDocumentDefaults(context);
      const selection = context.document.getSelection();
      selection.load("text");
      const existingControl = selection.parentContentControlOrNullObject;
      existingControl.load("isNullObject,tag");
      await context.sync();

      // References is a repeating list, unlike Abstract/Keywords (one per
      // document) — clicking the button with the cursor inside ALREADY
      // tagged content (a stray Title, an old reference, anything) should
      // convert that content into one more reference entry, matching the
      // re-tag-in-place convention every other tag button already follows,
      // not insert a second "References" heading + placeholder section.
      if (type === "references" && !existingControl.isNullObject) {
        const existingMeta = metadataService.decode(existingControl.tag);
        if (existingMeta) {
          const updatedMeta = metadataService.create("reference", {
            id: existingMeta.id,
            createdAt: existingMeta.createdAt
          });
          const controlRange = existingControl.getRange("Content");
          await applyTypographyToRangeDeep(context, controlRange, specForMetadata(updatedMeta));
          existingControl.tag = metadataService.encode(updatedMeta);
          existingControl.title = this.titleForMetadata(updatedMeta);
          await metadataService.upsertDocumentIndex(context, updatedMeta);
          await context.sync();
          return updatedMeta;
        }
      }

      const label = labelForHeading(type);
      const bodyPrompt =
        type === "abstract"
          ? "Type abstract body here"
          : type === "keywords"
            ? "Type comma-separated keywords here"
            : "Type references here";

      const bodyMetadata = metadataService.create(type);
      const headingMetadata = metadataService.create("section", { level: 2 });

      if (selection.text.trim().length > 0) {
        const headingRange = selection.insertText(`${label}\n`, Word.InsertLocation.before);
        await applyTypographyToRangeDeep(
          context,
          headingRange,
          type === "abstract"
            ? typographyByType.abstract_heading
            : type === "keywords"
              ? typographyByType.keywords_heading
              : typographyByType.references_heading
        );
        const headingControl = headingRange.insertContentControl();
        headingControl.title = `Libertatem ${label} Heading`;
        headingControl.tag = metadataService.encode(headingMetadata);
        headingControl.appearance = Word.ContentControlAppearance.hidden;

        await applyTypographyToRangeDeep(context, selection, specForMetadata(bodyMetadata));
        const bodyControl = selection.insertContentControl();
        bodyControl.title = this.titleForMetadata(bodyMetadata);
        bodyControl.tag = metadataService.encode(bodyMetadata);
        bodyControl.appearance = Word.ContentControlAppearance.boundingBox;
        await metadataService.upsertDocumentIndex(context, headingMetadata);
        await metadataService.upsertDocumentIndex(context, bodyMetadata);
        await context.sync();
        return bodyMetadata;
      }

      const headingRange = selection.insertText(`${label}\n`, Word.InsertLocation.replace);
      await applyTypographyToRangeDeep(
        context,
        headingRange,
        type === "abstract"
          ? typographyByType.abstract_heading
          : type === "keywords"
            ? typographyByType.keywords_heading
            : typographyByType.references_heading
      );
      const headingControl = headingRange.insertContentControl();
      headingControl.title = `Libertatem ${label} Heading`;
      headingControl.tag = metadataService.encode(headingMetadata);
      headingControl.appearance = Word.ContentControlAppearance.hidden;

      const bodyRange = headingRange.insertText(`${bodyPrompt}\n`, Word.InsertLocation.after);
      await applyTypographyToRangeDeep(context, bodyRange, specForMetadata(bodyMetadata));
      const bodyControl = bodyRange.insertContentControl();
      bodyControl.title = this.titleForMetadata(bodyMetadata);
      bodyControl.tag = metadataService.encode(bodyMetadata);
      bodyControl.appearance = Word.ContentControlAppearance.boundingBox;

      const after = bodyRange.insertParagraph("", Word.InsertLocation.after);
      after.font.name = bodyFont();
      after.font.size = 11;
      after.alignment = "Justified";
      after.lineSpacing = 12;
      bodyRange.select("Select");

      await metadataService.upsertDocumentIndex(context, headingMetadata);
      await metadataService.upsertDocumentIndex(context, bodyMetadata);
      await context.sync();
      return bodyMetadata;
    });
  }

  // Remove the semantic tag(s) at the cursor, keeping the text. Use to undo a
  // wrong tag (e.g. a paragraph accidentally tagged as a figure). After untagging,
  // run Update Styles (or re-tag) to normalize the text back to a body paragraph.
  async removeTagAtSelection(): Promise<number> {
    return Word.run(async (context) => {
      const selection = context.document.getSelection();
      // Collect every semantic control touching the selection: the parent (cursor
      // sits inside it) AND any controls the selection contains (selection spans
      // or equals a control — then it isn't the "parent"). Covers both ways a
      // user lands on a tag.
      const inside = selection.contentControls;
      inside.load("items/id,items/tag");
      const parent = selection.parentContentControlOrNullObject;
      parent.load("isNullObject,id,tag");
      await context.sync();

      const targets: Word.ContentControl[] = [];
      if (!parent.isNullObject) targets.push(parent);
      for (const control of inside.items) targets.push(control);

      const seen = new Set<number>();
      const removedIds: string[] = [];
      for (const control of targets) {
        if (seen.has(control.id)) continue;
        // Only act on OUR semantic controls.
        if (!metadataService.decode(control.tag)) continue;
        seen.add(control.id);
        const meta = metadataService.decode(control.tag);
        if (meta?.id) removedIds.push(meta.id);
        // delete(keepContent): true = drop the tag wrapper, keep the text/image.
        control.delete(true);
      }
      await context.sync();

      if (seen.size === 0) throw new Error("No tag at the cursor. Click inside the tagged content first.");

      // Forget the removed tags in the document index so exports/validation no
      // longer treat the content as tagged.
      await metadataService.removeFromDocumentIndex(context, removedIds);
      await context.sync();
      return seen.size;
    });
  }

  async toggleFormatting(format: "bold" | "italic" | "underline" | "superscript" | "subscript"): Promise<void> {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.font.load("bold,italic,underline,superscript,subscript");
      await context.sync();

      if (format === "bold") selection.font.bold = !selection.font.bold;
      if (format === "italic") selection.font.italic = !selection.font.italic;
      if (format === "underline") {
        selection.font.underline =
          selection.font.underline === Word.UnderlineType.none ? Word.UnderlineType.single : Word.UnderlineType.none;
      }
      if (format === "superscript") selection.font.superscript = !selection.font.superscript;
      if (format === "subscript") selection.font.subscript = !selection.font.subscript;

      await context.sync();
    });
  }

  async readSemanticControls(): Promise<Array<{ text: string; metadata: SemanticMetadata }>> {
    return Word.run(async (context) => {
      const controls = context.document.contentControls;
      controls.load("items/text,tag,title");
      await context.sync();
      return controls.items
        .map((control) => ({ text: control.text.trim(), metadata: metadataService.decode(control.tag) }))
        .filter((item): item is { text: string; metadata: SemanticMetadata } => item.metadata !== null);
    });
  }

  async latestSemanticId(type: SemanticType): Promise<string | undefined> {
    return Word.run(async (context) => {
      const index = await metadataService.readDocumentIndex(context);
      return [...index]
        .reverse()
        .find((item) => item.type === type)?.id;
    });
  }

  async readParagraphs(): Promise<string[]> {
    return Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items/text");
      await context.sync();
      return paragraphs.items.map((item) => item.text.trim()).filter(Boolean);
    });
  }

  async autoTagDocument(): Promise<{ headings: number; paragraphs: number; figures: number; captions: number; tables: number; blockquotes: number; authors: number; footnotes: number; equations: number; skipped: number }> {
    return Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      const bodyOoxml = context.document.body.getOoxml();
      paragraphs.load("items");
      await context.sync();

      const fonts = paragraphs.items.map((p) => { p.font.load("size,bold,italic,allCaps"); return p.font; });
      const parents = paragraphs.items.map((p) => {
        const parent = p.parentContentControlOrNullObject;
        parent.load("isNullObject,tag,cannotEdit");
        return parent;
      });
      const picColls = paragraphs.items.map((p) => {
        const pics = p.inlinePictures;
        pics.load("items");
        return pics;
      });
      // Word's automatic numbering ("3.", "3.1.") is NOT part of paragraph.text,
      // so heading depth must also come from the list item, not just the text.
      const headingListItems = paragraphs.items.map((p) => {
        const li = p.listItemOrNullObject;
        li.load("isNullObject,level,listString");
        return li;
      });
      const headingLists = paragraphs.items.map((p) => {
        const l = p.listOrNullObject;
        l.load("isNullObject,id");
        return l;
      });
      paragraphs.items.forEach((p) => p.load("text,styleBuiltIn,tableNestingLevel"));
      await context.sync();

      // A native Word equation object (Insert > Equation, OOXML <m:oMath>)
      // that isn't inside any of our content controls must be tagged
      // "equation", not fall through to the generic paragraph branch below.
      // Matched by text against a small multiset built from ONE body-level
      // getOoxml() call — not a per-paragraph getOoxml() (slow, and
      // unreliable on a real multi-hundred-paragraph document) and not a
      // whole-document positional/text-alignment pass (desyncs on a single
      // mismatched paragraph anywhere earlier and silently misclassifies
      // everything after it — how equations kept coming out "paragraph").
      const eqTextCounts = untaggedEquationTextCounts(bodyOoxml.value);
      const hasEquationAt = paragraphs.items.map((p) => {
        const key = paragraphKey(p.text);
        const count = eqTextCounts.get(key) ?? 0;
        if (count <= 0) return false;
        eqTextCounts.set(key, count - 1);
        return true;
      });

      // Determine body font size (mode of untagged paragraph sizes)
      const sizeCounts = new Map<number, number>();
      for (let i = 0; i < paragraphs.items.length; i++) {
        if (!parents[i].isNullObject && metadataService.decode(parents[i].tag)) continue;
        const sz = fonts[i].size;
        if (sz && sz > 0) sizeCounts.set(sz, (sizeCounts.get(sz) || 0) + 1);
      }
      let bodySize = 12;
      let maxCount = 0;
      for (const [sz, count] of sizeCounts) {
        if (count > maxCount) { maxCount = count; bodySize = sz; }
      }

      const headingSizes = [...new Set([...sizeCounts.keys()].filter((s) => s > bodySize))].sort((a, b) => b - a);
      const sizeToLevel = new Map<number, number>();
      headingSizes.forEach((sz, idx) => sizeToLevel.set(sz, Math.min(idx + 1, 6)));

      const styleToHeadingLevel = new Map<string, number>([
        ["Heading1", 1], ["Heading2", 2], ["Heading3", 3],
        ["Heading4", 4], ["Heading5", 5], ["Heading6", 6],
      ]);
      // A caption label is "Table 2." / "Table 2:" / "Table 2 – …" / "Table 2 Title"
      // (capitalized title) or a bare "Table 2". A lowercase word right after the
      // number ("Table 2 summarizes…", "Figure 1 shows…") is a body sentence.
      const CAPTION_RE = /^(?:figure|Figure|FIGURE|fig|Fig|FIG|image|Image|IMAGE|img|Img|IMG|plate|Plate|PLATE|photo|Photo|PHOTO|illustration|Illustration|ILLUSTRATION)\s*\.?\s*\d+[A-Za-z]?(?:\s*[.:–—-]|\s*$|\s+(?![a-z]))/;
      const TABLE_CAPTION_RE = /^(?:table|Table|TABLE)\s*\.?\s*\d+[A-Za-z]?(?:\s*[.:–—-]|\s*$|\s+(?![a-z]))/;
      const BLOCKQUOTE_STYLES = new Set(["Quote", "IntenseQuote"]);
      const ABSTRACT_HEADINGS = new Set(["abstract"]);
      const KEYWORDS_HEADINGS = new Set(["keywords", "key words", "key-words", "index terms"]);
      const REFERENCES_HEADINGS = new Set(["references", "bibliography", "works cited", "sources", "reference", "literature cited", "endnotes"]);
      const FOOTNOTE_STYLE = "FootnoteText";

      // Author-zone detection patterns
      const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
      const ORCID_RE = /orcid\.org\/\d{4}-\d{4}-\d{4}-\d{3}[\dX]/i;
      const ROR_RE = /ror\.org\//i;
      const BY_NAME_RE = /^by\s+/i;
      const AFFILIATION_KEYWORDS = /\b(?:university|college|institute|department|faculty|school|centre|center|laboratory|lab|academy|hospital|foundation|research|professor|lecturer|fellow|phd|ph\.d|m\.?sc|b\.?sc|ll\.?b|ll\.?m|m\.?a|b\.?a|m\.?d|m\.?phil|dr\.|senior\s+lecturer|assistant\s+professor|associate\s+professor|dean)\b/i;
      const COUNTRY_RE = /\b(?:india|usa|uk|tanzania|kenya|nigeria|ghana|south\s+africa|bangladesh|pakistan|china|japan|brazil|canada|australia|germany|france|italy|spain|netherlands|sweden|switzerland|singapore|malaysia|indonesia|philippines|egypt|iran|iraq|jordan|saudi\s+arabia|uae|united\s+states|united\s+kingdom|new\s+zealand)\b/i;
      const ROLES_RE = /^(?:contributor\s+)?roles?:/i;

      // Helper: is text ALL CAPS? (at least 5 alpha chars, >80% uppercase)
      const isAllCaps = (t: string): boolean => {
        const alpha = t.replace(/[^a-zA-Z]/g, "");
        if (alpha.length < 5) return false;
        const upper = alpha.replace(/[^A-Z]/g, "").length;
        return upper / alpha.length > 0.8;
      };

      const metadataList: SemanticMetadata[] = [];
      let headingCount = 0, tagged = 0, figureCount = 0, captionCount = 0, tableCount = 0, blockquoteCount = 0, authorCount = 0, footnoteCount = 0, equationCount = 0, skipped = 0;
      const figureIndices = new Set<number>();

      for (let i = 0; i < paragraphs.items.length; i++) {
        if (!parents[i].isNullObject && metadataService.decode(parents[i].tag)) continue;
        if (picColls[i].items.length > 0) figureIndices.add(i);
      }
      // Pre-generate each figure's id up front (not when it's tagged) so a
      // caption that precedes its figure in document order can still link to
      // it — the main loop below tags in ascending index order.
      const figureIdByIndex = new Map<number, string>();
      figureIndices.forEach((idx) => figureIdByIndex.set(idx, `figure-${uuid()}`));

      // Tables: tag each whole top-level table as ONE "table" control (same as
      // the manual Table tag) — never per-cell paragraph/heading controls.
      // Cells of a table we own (freshly tagged or already wrapped) are skipped
      // by the main loop; tables holding pictures keep the old per-paragraph path.
      // tableIdAt maps every cell paragraph to its table id so a table caption
      // directly above/below can carry `parent` (same link the manual caption uses).
      const tableCellIdx = new Set<number>();
      const tableIdAt = new Map<number, string>();
      const tableRuns: Array<{ start: number; end: number }> = [];
      for (let i = 0; i < paragraphs.items.length; i++) {
        if ((paragraphs.items[i].tableNestingLevel || 0) > 0) {
          const last = tableRuns[tableRuns.length - 1];
          if (last && last.end === i - 1) last.end = i;
          else tableRuns.push({ start: i, end: i });
        }
      }
      const bodyTables = context.document.body.tables;
      bodyTables.load("items/nestingLevel");
      await context.sync();
      const topTables = bodyTables.items.filter((tbl) => tbl.nestingLevel === 1);
      if (tableRuns.length === topTables.length) {
        topTables.forEach((tbl, k) => {
          const run = tableRuns[k];
          let hasPicture = false;
          for (let j = run.start; j <= run.end; j++) if (figureIndices.has(j)) hasPicture = true;
          if (hasPicture) return;
          // Already wrapped? Look at the CELL paragraphs' nearest content control —
          // `table.getRange().parentContentControlOrNullObject` comes back null for a
          // control that wraps exactly the table, so trusting it re-wrapped the table
          // on every Auto-Tag re-run (nested duplicate table controls).
          let existingTableId: string | undefined;
          let foreign = false;
          let cellTagged = false;
          for (let j = run.start; j <= run.end; j++) {
            const pc = parents[j];
            if (pc.isNullObject) continue;
            const meta = metadataService.decode(pc.tag);
            if (!meta) { foreign = true; continue; }
            if (meta.type === "table") { existingTableId = meta.id; break; }
            cellTagged = true; // old per-cell tags — leave the table alone
          }
          let id: string | undefined;
          if (existingTableId) {
            id = existingTableId;
          } else if (foreign || cellTagged) {
            return;
          } else {
            id = `table-${uuid()}`;
            metadataList.push(this.queueSemanticTagOnRange(tbl.getRange(), { type: "table", metadata: { id } }));
            tableCount++;
          }
          for (let j = run.start; j <= run.end; j++) {
            tableCellIdx.add(j);
            if (id) tableIdAt.set(j, id);
          }
        });
      }

      // A "Table N"/"Figure N" line is a caption ONLY when it sits directly
      // above or below its table/figure (blank paragraphs between are ignored).
      // Anywhere else — inside body text — it stays an ordinary paragraph.
      const adjacentObject = (idx: number, dir: -1 | 1): { kind: "table" | "figure"; at: number } | null => {
        for (let j = idx + dir; j >= 0 && j < paragraphs.items.length; j += dir) {
          if ((paragraphs.items[j].tableNestingLevel || 0) > 0) return { kind: "table", at: j };
          if (picColls[j].items.length > 0) return { kind: "figure", at: j };
          if (paragraphs.items[j].text.trim()) return null;
        }
        return null;
      };
      // Caption may sit above OR below its object (authors differ). If an object of
      // the right kind is on both sides (e.g. caption below table 1 and directly
      // above table 2), the caption's own number decides; otherwise above wins.
      const captionTarget = (idx: number, kind: "table" | "figure", text: string): number | null => {
        const hits: number[] = [];
        for (const dir of [-1, 1] as const) {
          const hit = adjacentObject(idx, dir);
          if (hit && hit.kind === kind) hits.push(hit.at);
        }
        if (hits.length < 2) return hits.length ? hits[0] : null;
        const num = Number((text.match(/\d+/) || [])[0]);
        const ordinal = (at: number): number => {
          if (kind === "table") return tableRuns.findIndex((r) => at >= r.start && at <= r.end) + 1;
          let n = 0;
          for (let j = 0; j <= at; j++) if (picColls[j].items.length > 0) n++;
          return n;
        };
        return hits.find((at) => ordinal(at) === num) ?? hits[0];
      };

      // Self-heal old documents: a figure_caption control can be missing its
      // `parent` link (auto-tagged before this fix existed) or carrying a
      // STALE one (pointing at a figure id that no longer exists — e.g. the
      // image was untagged and re-tagged, which mints a fresh id). Either way
      // it's invisible to readFigures() forever, and re-running Auto-Tag
      // alone can't fix it because the main loop below skips any paragraph
      // already inside a tagged control. Walk every EXISTING tagged control
      // here (not just newly-detected ones) and link any orphaned
      // figure_caption to its nearest existing figure control by paragraph
      // distance.
      const existingFigureIdAt = new Map<number, string>();
      const captionEntries: Array<{ idx: number; meta: SemanticMetadata }> = [];
      for (let i = 0; i < paragraphs.items.length; i++) {
        if (parents[i].isNullObject) continue;
        const meta = metadataService.decode(parents[i].tag);
        if (!meta) continue;
        if (meta.type === "figure" && meta.id) existingFigureIdAt.set(i, meta.id);
        else if (meta.type === "figure_caption") captionEntries.push({ idx: i, meta });
      }
      const validFigureIds = new Set(existingFigureIdAt.values());
      const orphanCaptionIndices = captionEntries
        .filter((entry) => !entry.meta.parent || !validFigureIds.has(entry.meta.parent))
        .map((entry) => entry.idx);
      if (existingFigureIdAt.size > 0 && orphanCaptionIndices.length > 0) {
        const figureIdxList = [...existingFigureIdAt.keys()];
        for (const capIdx of orphanCaptionIndices) {
          let nearest = figureIdxList[0];
          let nearestDist = Math.abs(nearest - capIdx);
          for (const fIdx of figureIdxList) {
            const dist = Math.abs(fIdx - capIdx);
            if (dist < nearestDist) { nearest = fIdx; nearestDist = dist; }
          }
          const meta = metadataService.decode(parents[capIdx].tag)!;
          parents[capIdx].tag = metadataService.encode({ ...meta, parent: existingFigureIdAt.get(nearest), updatedAt: new Date().toISOString() });
        }
      }

      // ── Pre-scan: seed existing frontmatter, then detect missing title ──
      let abstractIdx = -1;
      let titleIdx = -1;

      // Pass 1 must include already-tagged controls. They define the boundary
      // even though the final tagging pass skips them.
      for (let i = 0; i < paragraphs.items.length; i++) {
        const t = paragraphs.items[i].text.trim();
        const lower = t.toLowerCase();
        const existingMetadata = parents[i].isNullObject ? null : metadataService.decode(parents[i].tag);
        if (existingMetadata?.type === "title" && titleIdx === -1) titleIdx = i;
        const isAbstractBoundary =
          existingMetadata?.type === "abstract" ||
          ABSTRACT_HEADINGS.has(lower) || lower === "abstract:" || /^abstract\b/i.test(lower);
        if (isAbstractBoundary && abstractIdx === -1) abstractIdx = i;
      }

      // Pass 2 runs only when no semantic Title already exists, and never scans
      // beyond Abstract. This prevents a prominent body heading such as
      // "Introduction" from becoming a replacement/second Title.
      const titleSearchEnd = abstractIdx >= 0 ? abstractIdx : paragraphs.items.length;
      let firstCandidateSeen = false;
      for (let i = 0; titleIdx === -1 && i < titleSearchEnd; i++) {
        const existingMetadata = parents[i].isNullObject ? null : metadataService.decode(parents[i].tag);
        if (existingMetadata) continue;
        const t = paragraphs.items[i].text.trim();
        const lower = t.toLowerCase();
        const style = String(paragraphs.items[i].styleBuiltIn || "");

        // Find title: first significant paragraph that is styled Title, or ALL CAPS + bold, or biggest font.
        // NEVER let a reserved frontmatter/section heading ("Abstract"/"Keywords"/
        // "References") be picked as the title — the real title paragraph often
        // reads font.size === null (multi-run), so it gets skipped and the next
        // large bold paragraph ("Abstract", 28pt) was being tagged as the title.
        const isReservedHeading =
          ABSTRACT_HEADINGS.has(lower) || lower === "abstract:" ||
          KEYWORDS_HEADINGS.has(lower) || REFERENCES_HEADINGS.has(lower);
        if (titleIdx === -1 && t.length >= 5 && !isReservedHeading) {
          const isFirstCandidate = !firstCandidateSeen;
          firstCandidateSeen = true;
          if (style === "Title") { titleIdx = i; break; }
          const sz = fonts[i].size || bodySize;
          const isBold = fonts[i].bold === true;
          const capsFlag = fonts[i].allCaps === true || isAllCaps(t);
          if (capsFlag && isBold && sz >= bodySize) { titleIdx = i; break; }
          if (sz > bodySize && sizeToLevel.has(sz) && sizeToLevel.get(sz) === 1) { titleIdx = i; break; }
          // Mixed-case display title clearly larger than body. bold flag is
          // unreliable for multi-run title paragraphs, so don't require it.
          if (sz >= bodySize * 1.5 && t.length < 200) { titleIdx = i; break; }
          // The very first standalone paragraph of the whole article, bold,
          // but typed at the SAME font size as the body — the source .docx
          // never gave the title its own larger size. Still the title, not a
          // body paragraph. Scoped to only the first candidate paragraph (not
          // any later bold paragraph) and a title-shaped length so a genuine
          // bold lead-in sentence deep in the body is never mistaken for one.
          if (isFirstCandidate && isBold && t.length < 200) { titleIdx = i; break; }
        }
      }

      // Author zone = paragraphs between title and abstract that match author patterns
      const authorZone = new Set<number>();
      // Collect superscript markers from author names (e.g. "Rahul Ranjan¹²" → {"1","2"})
      const authorSuperscriptMarkers = new Set<string>();
      // Unicode superscript digits: ⁰¹²³⁴⁵⁶⁷⁸⁹ and regular trailing digits/commas
      const SUPERSCRIPT_TRAIL_RE = /[⁰¹²³⁴⁵⁶⁷⁸⁹\d,*†‡§‖¶]+$/;
      const UNICODE_SUPER_MAP: Record<string, string> = { "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4", "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9" };
      if (titleIdx >= 0 && abstractIdx > titleIdx) {
        for (let i = titleIdx + 1; i < abstractIdx; i++) {
          if (!parents[i].isNullObject && metadataService.decode(parents[i].tag)) continue;
          const t = paragraphs.items[i].text.trim();
          if (!t || t.length < 2) continue;
          if (figureIndices.has(i)) continue;
          const lower = t.toLowerCase();
          if (KEYWORDS_HEADINGS.has(lower)) break;
          if (BY_NAME_RE.test(t) || EMAIL_RE.test(t) || ORCID_RE.test(t) || ROR_RE.test(t) ||
              ROLES_RE.test(t) || AFFILIATION_KEYWORDS.test(t) || COUNTRY_RE.test(t) ||
              (fonts[i].bold === true && t.length < 60 && !ABSTRACT_HEADINGS.has(lower)) ||
              (fonts[i].italic === true && t.length < 200 && AFFILIATION_KEYWORDS.test(t))) {
            authorZone.add(i);
          } else if (t.length < 60 && i === titleIdx + 1) {
            authorZone.add(i);
          } else if (t.length < 80 && authorZone.has(i - 1)) {
            authorZone.add(i);
          }
          // Extract superscript markers from author-zone lines
          if (authorZone.has(i)) {
            const trail = t.match(SUPERSCRIPT_TRAIL_RE);
            if (trail) {
              const normalized = [...trail[0]].map((ch) => UNICODE_SUPER_MAP[ch] || ch).join("");
              for (const marker of normalized.split(/[,\s]+/).filter(Boolean)) {
                if (/^\d+$/.test(marker)) authorSuperscriptMarkers.add(marker);
              }
            }
          }
        }
      }

      // ── Pre-scan: find footnote paragraphs ──
      const footnoteIndices = new Set<number>();
      for (let i = 0; i < paragraphs.items.length; i++) {
        if (!parents[i].isNullObject && metadataService.decode(parents[i].tag)) continue;
        const t = paragraphs.items[i].text.trim();
        if (!t) continue;
        const style = String(paragraphs.items[i].styleBuiltIn || "");
        const sz = fonts[i].size || bodySize;
        if (style === FOOTNOTE_STYLE) {
          footnoteIndices.add(i);
        } else if (sz < bodySize && sz <= 10 && /^\d+\s/.test(t) && t.length > 10) {
          footnoteIndices.add(i);
        }
      }

      // Reclassify footnotes whose leading number matches an author superscript marker
      // as author metadata (affiliation footnotes), not regular footnotes.
      const authorFootnotes = new Set<number>();
      if (authorSuperscriptMarkers.size > 0) {
        for (const idx of footnoteIndices) {
          const t = paragraphs.items[idx].text.trim();
          const leadingNum = t.match(/^(\d+)\s/);
          if (leadingNum && authorSuperscriptMarkers.has(leadingNum[1])) {
            authorFootnotes.add(idx);
            footnoteIndices.delete(idx);
          }
        }
      }

      // Helper: is this paragraph a heading?
      const isHeadingPara = (idx: number): boolean => {
        const s = String(paragraphs.items[idx].styleBuiltIn || "");
        if (styleToHeadingLevel.has(s) || s === "Title" || s === "Subtitle") return true;
        const sz = fonts[idx].size || bodySize;
        if (sizeToLevel.has(sz)) return true;
        const t = paragraphs.items[idx].text.trim();
        if (fonts[idx].bold === true && t.length < 80 && sz >= bodySize) return true;
        // ALL CAPS short text = heading even at same font size
        if ((fonts[idx].allCaps === true || isAllCaps(t)) && t.length < 80 && sz >= bodySize) return true;
        return false;
      };

      // ── Pre-scan: assign a level to every body section heading ──
      // Level is relative to the FIRST body heading after the keywords section
      // (the anchor = level 1). Numbered headings (1, 1.1, 1.1.1) use their
      // numbering depth directly; unnumbered ones are ranked by prominence
      // (font size, then bold as a tiebreak): same prominence as the anchor →
      // level 1, next-smaller distinct tier → level 2, next → level 3, etc.
      // References is tagged separately (always level 1) and excluded here.
      const numberingDepth = (t: string): number => {
        const m = t.match(/^\s*(\d+(?:\.\d+)*)[.)]?(?:\s|$)/);
        if (!m) return 0;
        return Math.min(m[1].split(".").filter(Boolean).length, 6);
      };

      // Depth of a paragraph's numbering: typed ("3.1 Title") or Word-automatic
      // (list string "3.1." / "A." / list level). 0 = not numbered. Bullets = 0.
      const nonEmptyNeighbor = (start: number, direction: -1 | 1): number => {
        for (let idx = start + direction; idx >= 0 && idx < paragraphs.items.length; idx += direction) {
          if (paragraphs.items[idx].text.trim()) return idx;
        }
        return -1;
      };
      // A Word list is a HEADING list (auto-numbered section numbering) only if
      // most of its items are heading-shaped: short, bold, no sentence-ending
      // punctuation, and followed by body text or a deeper-level item — never
      // by another item at the same/shallower level (that is an ordinary
      // numbered/bulleted list, whose items must stay body paragraphs).
      const listStats = new Map<string, { total: number; headingShaped: number }>();
      const listKey = (idx: number): string => (headingLists[idx].isNullObject ? "" : String(headingLists[idx].id));
      for (let i = 0; i < paragraphs.items.length; i++) {
        const key = listKey(i);
        if (!key || headingListItems[i].isNullObject) continue;
        const text = paragraphs.items[i].text.trim();
        if (!text) continue;
        const st = listStats.get(key) ?? { total: 0, headingShaped: 0 };
        st.total++;
        let shaped = fonts[i].bold !== false && text.length < 180 && !/[.!?:;,]\s*$/.test(text);
        if (shaped) {
          const nextIdx = nonEmptyNeighbor(i, 1);
          if (nextIdx >= 0 && listKey(nextIdx) === key && !headingListItems[nextIdx].isNullObject &&
              (headingListItems[nextIdx].level || 0) <= (headingListItems[i].level || 0)) shaped = false;
        }
        if (shaped) st.headingShaped++;
        listStats.set(key, st);
      }
      const isHeadingList = (idx: number): boolean => {
        const st = listStats.get(listKey(idx));
        return !!st && st.total >= 2 && st.headingShaped / st.total >= 0.6;
      };

      const numDepthAt = (idx: number): number => {
        const typed = numberingDepth(paragraphs.items[idx].text.trim());
        if (typed > 0) return typed;
        const li = headingListItems[idx];
        if (li.isNullObject || !isHeadingList(idx)) return 0;
        const ls = (li.listString || "").trim();
        const m = ls.match(/^\(?(\d+(?:\.\d+)*)[.)]?$/);
        if (m) {
          const parts = m[1].split(".").filter(Boolean).length;
          return Math.min(Math.max(parts, parts === 1 ? (li.level || 0) + 1 : parts), 6);
        }
        if (/^\(?[A-Za-z]{1,4}[.)]$/.test(ls)) return Math.min((li.level || 0) + 1, 6);
        return 0;
      };

      // Auto-numbered section heading: item of a heading list. Mixed-run bold
      // (bold === null, e.g. a non-breaking hyphen run) must not disqualify it.
      const isListHeading = (idx: number): boolean =>
        (paragraphs.items[idx].tableNestingLevel || 0) === 0 && !headingListItems[idx].isNullObject && isHeadingList(idx) && paragraphs.items[idx].text.trim().length < 250;
      // Table-cell paragraph or item of an ordinary bullet/numbered list: never a heading by heuristics
      // (all-caps / bold / long-line), only by explicit Heading style or size tier.
      const isPlainListItem = (idx: number): boolean =>
        (paragraphs.items[idx].tableNestingLevel || 0) > 0 ||
        (!headingListItems[idx].isNullObject && !isHeadingList(idx));

      // Long manually-formatted headings often use the Normal style and the
      // body font size, so boldness is their only typographic signal. The old
      // <80-character guard rejected legitimate 120-180-character headings.
      // Admit a long candidate only when it is structurally sandwiched between
      // normal body paragraphs. This keeps bold callouts, lists, frontmatter,
      // captions, and references out. `bold` may be null when the optional
      // number and title use mixed runs; null is accepted, explicit false is not.
      const referencesStartIdx = paragraphs.items.findIndex((paragraph) =>
        REFERENCES_HEADINGS.has(paragraph.text.trim().toLowerCase())
      );
      const isBodyNeighbor = (idx: number): boolean => {
        if (idx < 0 || figureIndices.has(idx) || footnoteIndices.has(idx) || authorZone.has(idx) || authorFootnotes.has(idx)) return false;
        const text = paragraphs.items[idx].text.trim();
        if (text.length <= 30 || /[\r\n\v\f\u2028\u2029]/.test(text)) return false;
        if (CAPTION_RE.test(text) || TABLE_CAPTION_RE.test(text)) return false;
        const lower = text.toLowerCase();
        if (ABSTRACT_HEADINGS.has(lower) || KEYWORDS_HEADINGS.has(lower) || REFERENCES_HEADINGS.has(lower)) return false;
        const style = String(paragraphs.items[idx].styleBuiltIn || "");
        if (styleToHeadingLevel.has(style) || style === "Title" || style === "Subtitle" || BLOCKQUOTE_STYLES.has(style)) return false;
        const size = fonts[idx].size || bodySize;
        const headingLikeEnding = !/[.!?]\s*$/.test(text);
        if (sizeToLevel.has(size) || (headingLikeEnding && fonts[idx].bold === true) || (headingLikeEnding && numDepthAt(idx) > 0)) return false;
        return true;
      };
      const longSandwichedHeadings = new Set<number>();
      for (let i = 0; i < paragraphs.items.length; i++) {
        if (!parents[i].isNullObject && metadataService.decode(parents[i].tag)) continue;
        if (i <= abstractIdx || (referencesStartIdx >= 0 && i >= referencesStartIdx)) continue;
        if (figureIndices.has(i) || footnoteIndices.has(i) || authorZone.has(i) || authorFootnotes.has(i)) continue;
        if (isPlainListItem(i)) continue;
        const text = paragraphs.items[i].text.trim();
        if (text.length < 80 || text.length > 180) continue;
        if (/[\r\n\v\f\u2028\u2029]/.test(text) || /[.!?]\s*$/.test(text)) continue;
        if (CAPTION_RE.test(text) || TABLE_CAPTION_RE.test(text)) continue;
        const style = String(paragraphs.items[i].styleBuiltIn || "");
        if (BLOCKQUOTE_STYLES.has(style)) continue;
        if (fonts[i].bold === false) continue;
        const prev = nonEmptyNeighbor(i, -1);
        const next = nonEmptyNeighbor(i, 1);
        if (isBodyNeighbor(prev) && isBodyNeighbor(next)) longSandwichedHeadings.add(i);
      }

      const bodyHeadings: Array<{ idx: number; prom: number }> = [];
      for (let i = 0; i < paragraphs.items.length; i++) {
        if (!parents[i].isNullObject && metadataService.decode(parents[i].tag)) continue;
        if (i === titleIdx || authorZone.has(i) || authorFootnotes.has(i) || footnoteIndices.has(i) || figureIndices.has(i)) continue;
        const t = paragraphs.items[i].text.trim();
        if (!t) continue;
        if (CAPTION_RE.test(t) || TABLE_CAPTION_RE.test(t)) continue;
        const lower = t.toLowerCase();
        // Reserved frontmatter / references headings are handled elsewhere.
        if (
          ABSTRACT_HEADINGS.has(lower) || lower === "abstract:" ||
          KEYWORDS_HEADINGS.has(lower) || /^key\s*words?\s*:?\s*$/i.test(lower) ||
          REFERENCES_HEADINGS.has(lower) ||
          /^key\s*words?\s*:\s*\S/i.test(t) || /^abstract\s*[:.–—-]\s*\S/i.test(t)
        ) continue;
        const style = String(paragraphs.items[i].styleBuiltIn || "");
        const sz = fonts[i].size || bodySize;
        const isStyledHeading = styleToHeadingLevel.has(style) || style === "Title" || style === "Subtitle";
        const capsFlag = fonts[i].allCaps === true || isAllCaps(t);
        const plainList = isPlainListItem(i);
        const looksLikeHeading =
          isStyledHeading || sizeToLevel.has(sz) || isListHeading(i) ||
          (!plainList && fonts[i].bold === true && t.length < 80 && sz >= bodySize) ||
          (!plainList && capsFlag && t.length < 80 && sz >= bodySize) ||
          longSandwichedHeadings.has(i);
        if (!looksLikeHeading || style === "Title" || style === "Subtitle") continue;
        // Prominence: font size dominates; bold breaks ties within a size.
        bodyHeadings.push({ idx: i, prom: sz * 100 + (fonts[i].bold === true ? 1 : 0) });
      }
      const headingLevel = new Map<number, number>();
      if (bodyHeadings.length > 0) {
        const anchorProm = bodyHeadings[0].prom;
        const smallerTiers = [...new Set(bodyHeadings.map((h) => h.prom).filter((p) => p < anchorProm))].sort((a, b) => b - a);
        // Unnumbered heading inside a numbered document nests under the
        // preceding numbered heading (e.g. bold "Future Research" after "15.").
        let lastNumLevel = 0;
        let lastNumProm = 0;
        for (const h of bodyHeadings) {
          const depth = numDepthAt(h.idx);
          let level = longSandwichedHeadings.has(h.idx) && depth === 0
            ? 1
            : depth > 0
            ? depth
            : h.prom >= anchorProm ? 1 : Math.min(2 + smallerTiers.indexOf(h.prom), 6);
          if (depth > 0) {
            lastNumLevel = depth;
            lastNumProm = h.prom;
          } else if (lastNumLevel > 0 && h.prom <= lastNumProm) {
            level = Math.min(lastNumLevel + 1, 6);
          }
          headingLevel.set(h.idx, Math.min(Math.max(level, 1), 6));
        }
      }

      let region: "none" | "abstract" | "keywords" | "references" = "none";

      for (let i = 0; i < paragraphs.items.length; i++) {
        const parent = parents[i];
        const text = paragraphs.items[i].text.trim();
        const style = String(paragraphs.items[i].styleBuiltIn || "");
        if (!parent.isNullObject && metadataService.decode(parent.tag)) { skipped++; continue; }
        if (tableCellIdx.has(i)) { skipped++; continue; }
        // A paragraph nested inside ANY foreign (non-Libertatem) content
        // control the author inserted themselves — a native rich-text
        // control, a TOC field, or (confirmed against a real test file)
        // Word's own Citations & Bibliography FIELD ({ BIBLIOGRAPHY } inside
        // <w:sdt><w:bibliography/></w:sdt>) — can't be trusted to safely
        // accept a NEW nested content control. This is NOT limited to locked
        // (cannotEdit) controls: the real crash came from a bibliography
        // field control that reported cannotEdit === false and still
        // rejected the insert with a bare "GeneralException
        // (Range.insertContentControl)" — Word can refuse nesting into a
        // live field-result region regardless of the lock flag, with no way
        // to detect that in advance. One failing paragraph anywhere kills the
        // ENTIRE Auto-Tag batch (all paragraphs share one context.sync()), so
        // skip tagging ANY paragraph already inside a foreign control rather
        // than risk it — its plain text still exports fine untagged (and
        // readBodyOutline/readReferences have their own text-position
        // fallback for a References/Bibliography list specifically).
        if (!parent.isNullObject) { skipped++; continue; }

        // Title (detected in pre-scan)
        if (i === titleIdx) {
          region = "none";
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "title" }, text));
          headingCount++;
          continue;
        }

        // Author zone paragraphs
        if (authorZone.has(i)) {
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "author" }, text));
          authorCount++;
          continue;
        }

        // Footnotes that are actually author affiliation footnotes (matched by superscript marker)
        if (authorFootnotes.has(i)) {
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "author" }, text));
          authorCount++;
          continue;
        }

        // Footnotes
        if (footnoteIndices.has(i)) {
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "footnote" }, text));
          footnoteCount++;
          continue;
        }

        // Figure: paragraph contains an inline image
        if (figureIndices.has(i)) {
          region = "none";
          const range = paragraphs.items[i].getRange("Whole");
          const figMeta = this.queueSemanticTagOnRange(
            range,
            { type: "figure", metadata: { id: figureIdByIndex.get(i) } },
            text
          );
          metadataList.push(figMeta);
          figureCount++;
          continue;
        }

        // Figure/image caption — link it to the adjacent figure via `parent` so
        // readFigures() can find it (caption.metadata.parent === figure.metadata.id).
        // Without this link the caption is tagged but never rendered in the PDF.
        const figureAt = captionTarget(i, "figure", text);
        if ((CAPTION_RE.test(text) && figureAt !== null) || (text.length < 200 && (figureIndices.has(i - 1) || figureIndices.has(i + 1)) && fonts[i].size && fonts[i].size < bodySize)) {
          const range = paragraphs.items[i].getRange("Whole");
          const parentFigureId = (figureAt !== null ? figureIdByIndex.get(figureAt) : undefined) ?? figureIdByIndex.get(i - 1) ?? figureIdByIndex.get(i + 1);
          metadataList.push(
            this.queueSemanticTagOnRange(range, { type: "figure_caption", metadata: { parent: parentFigureId } }, text)
          );
          captionCount++;
          continue;
        }

        // Table caption
        const tableAt = TABLE_CAPTION_RE.test(text) ? captionTarget(i, "table", text) : null;
        if (tableAt !== null) {
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "table_caption", metadata: { parent: tableIdAt.get(tableAt) } }, text));
          captionCount++;
          continue;
        }

        const lower = text.toLowerCase().trim();
        const isStyledHeading = styleToHeadingLevel.has(style) || style === "Title" || style === "Subtitle";
        const sz = fonts[i].size || bodySize;
        const isBold = fonts[i].bold === true;
        const capsFlag = fonts[i].allCaps === true || isAllCaps(text);
        const isHeadingBySize = sizeToLevel.has(sz);
        const plainListItem = isPlainListItem(i);
        const isHeadingByBold = !plainListItem && isBold && text.length < 80 && sz >= bodySize;
        const isHeadingByCaps = !plainListItem && capsFlag && text.length < 80 && sz >= bodySize;
        const looksLikeHeading = isStyledHeading || isHeadingBySize || isHeadingByBold || isHeadingByCaps || longSandwichedHeadings.has(i) || isListHeading(i);

        // Keywords: detect "Key words:" or "Keywords:" as inline prefix (bold
        // prefix, not standalone heading). Require actual content after the colon
        // — a bare "Keywords:" on its own line is a standalone heading (handled
        // below), and tagging it here would capture empty keyword content and
        // leave the real keywords paragraph untagged.
        if (/^key\s*words?\s*:\s*\S/i.test(text) && !looksLikeHeading) {
          region = "none";
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "keywords" }, text));
          tagged++;
          continue;
        }

        // Run-in abstract: a bold "Abstract:" label at the START of the same
        // paragraph as the abstract body (one Word paragraph, a very common
        // journal format), not a standalone heading. The label is either
        // "Abstract:" (colon/period/dash) OR "Abstract" alone on its own line
        // (a soft line break before the body — Office.js reports it as \v/\r/\n).
        // A plain space after "Abstract" does NOT count, so a real sentence or
        // section title starting with the word "Abstract" ("Abstract reasoning
        // in…") is never captured. Require length > 40 so a short standalone
        // "Abstract" heading (handled below) isn't caught. The exporter strips
        // the leading label so the panel shows only the body.
        if (/^\s*abstract(?:\s*[:.\u2013\u2014-]|[\r\n\v\f\u2028\u2029])/i.test(text) && text.length > 40) {
          region = "none";
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "abstract" }, text));
          tagged++;
          continue;
        }

        // Abstract heading. An exact standalone "Abstract" / "Abstract:" is
        // unambiguous, so recognize it even when looksLikeHeading is false —
        // Word's paragraph-level bold flag is null for mixed-run paragraphs, so
        // a genuinely bold "Abstract" heading can otherwise slip through and its
        // body gets mis-tagged as a plain paragraph. Tag with the same control
        // title the manual insert uses so the exporter drops it from the body
        // (the frontmatter panel already renders its own "Abstract" label).
        if (ABSTRACT_HEADINGS.has(lower) || lower === "abstract:") {
          region = "abstract";
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "section", metadata: { level: 1 }, controlTitle: "Libertatem Abstract Heading" }, text));
          headingCount++;
          continue;
        }

        // Keywords heading (standalone, e.g. "Keywords" or "Keywords:"). Same
        // relaxed detection + control-title strip as the abstract heading above.
        if (KEYWORDS_HEADINGS.has(lower) || /^key\s*words?\s*:?\s*$/i.test(lower)) {
          region = "keywords";
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "section", metadata: { level: 1 }, controlTitle: "Libertatem Keywords Heading" }, text));
          headingCount++;
          continue;
        }

        // References heading — tag as references section, all following paragraphs
        // as individual references. An exact standalone "References"/"Bibliography"/
        // "Endnotes" etc. is unambiguous, so (matching the Abstract/Keywords
        // headings above) recognize it even when looksLikeHeading is false — a
        // heading typed at body size/weight (no bold, no larger font) would
        // otherwise never be detected, silently leaving the whole reference list
        // untagged as plain body paragraphs.
        if (REFERENCES_HEADINGS.has(lower)) {
          region = "references";
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "section", metadata: { level: 1 } }, text));
          headingCount++;
          continue;
        }

        // Any other heading. Never fire once inside the references region —
        // a pasted reference entry can carry a stray larger font size or
        // accidental bold from its source (web page/PDF), which otherwise
        // gets misread as a new section heading mid-reference-list instead
        // of tagged as a reference (see region === "references" below).
        if (looksLikeHeading && text.length >= 1 && region !== "references") {
          region = "none";

          if (BLOCKQUOTE_STYLES.has(style)) {
            if (text.length >= 3) {
              const range = paragraphs.items[i].getRange("Content");
              metadataList.push(this.queueSemanticTagOnRange(range, { type: "blockquote" }, text));
              blockquoteCount++;
            }
            continue;
          }

          if (style === "Title") {
            const range = paragraphs.items[i].getRange("Whole");
            metadataList.push(this.queueSemanticTagOnRange(range, { type: "title" }, text));
            headingCount++;
            continue;
          }
          if (style === "Subtitle") {
            const range = paragraphs.items[i].getRange("Whole");
            metadataList.push(this.queueSemanticTagOnRange(range, { type: "subtitle" }, text));
            headingCount++;
            continue;
          }

          // Level comes from the body-heading pre-scan: the first heading after
          // keywords is the level-1 anchor, others rank by numbering depth or by
          // font size + boldness relative to it. Fall back to level 1 if the
          // pre-scan didn't classify this paragraph (shouldn't happen for a real
          // heading, but never emit an undefined level).
          const level = (headingLevel.get(i) ?? 1) as 1 | 2 | 3 | 4 | 5 | 6;
          const range = paragraphs.items[i].getRange("Whole");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "section", metadata: { level } }, text));
          headingCount++;
          continue;
        }

        if (!text || (text.length < 3 && headingListItems[i].isNullObject)) continue;

        if (region === "abstract") {
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "abstract" }, text));
          tagged++;
          continue;
        }

        if (region === "keywords") {
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "keywords" }, text));
          tagged++;
          continue;
        }

        if (region === "references") {
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "reference" }, text));
          tagged++;
          continue;
        }

        if (hasEquationAt[i]) {
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "equation" }, text));
          equationCount++;
          continue;
        }

        if (BLOCKQUOTE_STYLES.has(style)) {
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "blockquote" }, text));
          blockquoteCount++;
          continue;
        }

        // Bullet/numbered list items are tagged at any length (the exporter reads
        // the Word list info off the paragraph); plain body text needs >30 chars, or a
        // trailing colon (a short lead-in line such as "Examples include:").
        if (text.length > 30 || !headingListItems[i].isNullObject || /:\s*$/.test(text)) {
          const range = paragraphs.items[i].getRange("Content");
          metadataList.push(this.queueSemanticTagOnRange(range, { type: "paragraph" }, text));
          tagged++;
        }
      }

      if (metadataList.length > 0) {
        await metadataService.upsertDocumentIndexMany(context, metadataList);
      }
      await context.sync();
      return { headings: headingCount, paragraphs: tagged, figures: figureCount, captions: captionCount, tables: tableCount, blockquotes: blockquoteCount, authors: authorCount, footnotes: footnoteCount, equations: equationCount, skipped };
    });
  }

  // Reclassifies an already-tagged "paragraph" control that actually contains
  // a native Word equation object (<m:oMath>) — most commonly one tagged by a
  // run of Auto-Tag that predates equation detection. Finds targets by
  // parsing the body's OOXML ONCE and reading each content control's own
  // `id` straight out of its `<w:tag>` attribute — no per-paragraph
  // `getOoxml()` calls (slow and, on a real multi-hundred-paragraph document,
  // apparently unreliable) and no text/position matching to desync. Runs as
  // its own Word.run/sync, separate from autoTagDocument's single giant
  // batch, so one bad insert elsewhere can't silently swallow this fix too.
  async healEquationTags(): Promise<{ healed: number; reverted: number; candidates: number; ooxmlLength: number }> {
    return Word.run(async (context) => {
      const bodyOoxml = context.document.body.getOoxml();
      await context.sync();

      const toEquation = findEquationTaggedAsParagraph(bodyOoxml.value);
      const toParagraph = findParagraphTaggedAsEquation(bodyOoxml.value);
      const diag = { healed: 0, reverted: 0, candidates: toEquation.size, ooxmlLength: bodyOoxml.value.length };
      if (toEquation.size === 0 && toParagraph.size === 0) return diag;

      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();

      for (const control of controls.items) {
        const existingMeta = metadataService.decode(control.tag);
        if (!existingMeta?.id) continue;

        let newType: SemanticType | undefined;
        if (toEquation.has(existingMeta.id)) newType = "equation";
        else if (toParagraph.has(existingMeta.id)) newType = "paragraph";
        if (!newType) continue;

        const updatedMeta: SemanticMetadata = { ...existingMeta, type: newType, updatedAt: new Date().toISOString() };
        control.tag = metadataService.encode(updatedMeta);
        control.title = this.titleForMetadata(updatedMeta);
        applyTypographyToRange(control.getRange("Content"), specForMetadata(updatedMeta));
        await metadataService.upsertDocumentIndex(context, updatedMeta);
        if (newType === "equation") diag.healed++;
        else diag.reverted++;
      }
      await context.sync();
      return diag;
    });
  }

  async captureCurrentParagraph(): Promise<{ bookmarkName: string; previewText: string; isAlreadyTagged: boolean }> {
    return Word.run(async (context) => {
      const selection = context.document.getSelection();
      const paragraph = selection.paragraphs.getFirst();
      paragraph.load("text");
      const parent = paragraph.parentContentControlOrNullObject;
      parent.load("isNullObject,tag");
      await context.sync();
      const isAlreadyTagged = !parent.isNullObject && metadataService.decode(parent.tag) !== null;
      const bookmarkName = `libertatem_batch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      paragraph.getRange("Whole").insertBookmark(bookmarkName);
      await context.sync();
      return { bookmarkName, previewText: paragraph.text.trim().slice(0, 80), isAlreadyTagged };
    });
  }

  async applyTagToBatch(bookmarkNames: string[], options: ApplySemanticOptions): Promise<{ tagged: number; skipped: number }> {
    return Word.run(async (context) => {
      const spec = specForMetadata(metadataService.create(options.type, options.metadata));
      if (!spec.preserveRuns) this.applyDocumentDefaults(context);
      const ranges: Array<{ range: Word.Range; name: string }> = [];
      for (const name of bookmarkNames) {
        const range = context.document.getBookmarkRangeOrNullObject(name);
        range.load("isNullObject,text");
        ranges.push({ range, name });
      }
      await context.sync();

      const valid = ranges.filter((r) => !r.range.isNullObject);
      const parentChecks = valid.map((r) => {
        const parent = r.range.parentContentControlOrNullObject;
        parent.load("isNullObject,tag");
        return parent;
      });
      await context.sync();

      const metadataList: SemanticMetadata[] = [];
      let skipped = 0;
      for (let i = 0; i < valid.length; i++) {
        if (!parentChecks[i].isNullObject && metadataService.decode(parentChecks[i].tag)) {
          skipped++;
          continue;
        }
        const text = valid[i].range.text?.trim() || "";
        const targetRange = options.type === "paragraph"
          ? valid[i].range.paragraphs.getFirst().getRange("Content")
          : valid[i].range.paragraphs.getFirst().getRange("Whole");
        metadataList.push(this.queueSemanticTagOnRange(targetRange, options, text));
      }
      if (metadataList.length > 0) {
        await metadataService.upsertDocumentIndexMany(context, metadataList);
      }
      await context.sync();

      for (const name of bookmarkNames) {
        try { context.document.deleteBookmark(name); } catch { /* already gone */ }
      }
      await context.sync();
      return { tagged: metadataList.length, skipped };
    });
  }

  async cleanupBatchBookmarks(bookmarkNames: string[]): Promise<void> {
    return Word.run(async (context) => {
      for (const name of bookmarkNames) {
        try { context.document.deleteBookmark(name); } catch { /* ok */ }
      }
      await context.sync();
    });
  }

  // Walk the body in document order, tagging each paragraph with the semantic
  // type of its innermost content control (or null when untagged). Lets the
  // exporter rebuild the block stream in true reading order — so untagged
  // paragraphs are emitted as body text in place, not dropped.
  async readBodyOutline(): Promise<
    Array<{
      text: string;
      richText?: string;
      type: SemanticType | null;
      level?: number;
      id?: string;
      controlId?: number;
      controlTitle?: string;
      hasImage: boolean;
      hasEquation: boolean;
      list?: { ordered: boolean; level: number };
    }>
  > {
    return Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      const bodyOoxml = context.document.body.getOoxml();
      paragraphs.load("items/text");
      await context.sync();

      const richParagraphs = parseRichParagraphsFromOoxml(bodyOoxml.value);
      const alignedRich = alignRichParagraphs(
        paragraphs.items.map((p) => p.text),
        richParagraphs
      );

      const parents = paragraphs.items.map((p) => p.parentContentControlOrNullObject);
      parents.forEach((c) => c.load("isNullObject,tag,id,title"));
      const pictureColls = paragraphs.items.map((p) => p.inlinePictures);
      pictureColls.forEach((pc) => pc.load("items"));
      const listItems = paragraphs.items.map((p) => p.listItemOrNullObject);
      listItems.forEach((li) => li.load("isNullObject,level,listString"));
      await context.sync();

      // Only matters for paragraphs that come out untagged (type null) below
      // — an already-tagged control's type is read straight off its own tag.
      // Matched by text against a small multiset from the body OOXML already
      // fetched above, not a per-paragraph getOoxml() call.
      const eqTextCounts = untaggedEquationTextCounts(bodyOoxml.value);
      const hasEquationAt = paragraphs.items.map((p) => {
        const key = paragraphKey(p.text);
        const count = eqTextCounts.get(key) ?? 0;
        if (count <= 0) return false;
        eqTextCounts.set(key, count - 1);
        return true;
      });

      // Fallback for a References/Bibliography list Auto-Tag couldn't (or
      // hasn't yet) wrap in our own content controls — most commonly Word's
      // own native Citations & Bibliography feature (Insert > Bibliography),
      // whose entries live inside a FIELD-backed content control ({
      // BIBLIOGRAPHY } fldChar/instrText). We can't safely nest a NEW content
      // control into a live field result — Word can reject the insert, and
      // "Update Field" would silently wipe any control we did manage to add
      // — so classify these paragraphs as references at READ time instead of
      // relying on Word-side tagging. Purely a read-time classification, no
      // document mutation, so it's robust regardless of what auto-tag can or
      // can't do to the Word side for this specific block. Every paragraph
      // still untagged (type null) after the FIRST exact-match References/
      // Bibliography/Endnotes heading is treated as a reference entry.
      const REFERENCES_HEADING_RE = /^(?:references|bibliography|works?\s+cited|endnotes?|sources|reference|literature\s+cited)$/i;
      let referencesHeadingIdx = -1;
      for (let i = 0; i < paragraphs.items.length; i++) {
        const t = paragraphs.items[i].text.replace(/\x1E/g, "-").replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "").trim();
        if (REFERENCES_HEADING_RE.test(t)) { referencesHeadingIdx = i; break; }
      }

      return paragraphs.items.map((p, i) => {
        const parent = parents[i];
        const meta = parent.isNullObject ? null : metadataService.decode(parent.tag);
        const plain = p.text.replace(/\x1E/g, "-").replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "").trim();
        const li = listItems[i];
        // A numbered/lettered/roman list marker renders as digits/letters
        // followed by punctuation ("1.", "a)", "iv."); anything else (a bullet
        // glyph, or an empty listString for picture bullets) is unordered.
        const list = li.isNullObject
          ? undefined
          : {
              ordered: /^[0-9]+[.)]|^[A-Za-z][.)]|^[ivxlcdmIVXLCDM]+[.)]/.test((li.listString || "").trim()),
              level: li.level ?? 0
            };
        let type = meta?.type ?? null;
        if (type === null && plain && referencesHeadingIdx >= 0 && i > referencesHeadingIdx) {
          type = "reference";
        }
        return {
          text: plain,
          richText: alignedRich[i],
          type,
          level: meta?.level,
          id: meta?.id,
          controlId: parent.isNullObject ? undefined : parent.id,
          controlTitle: parent.isNullObject ? undefined : parent.title,
          hasImage: pictureColls[i].items.length > 0,
          hasEquation: hasEquationAt[i],
          list
        };
      });
    });
  }

  async readSemanticParagraphs(): Promise<string[]> {
    return Word.run(async (context) => {
      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();

      const semanticControls = controls.items.filter((control) => metadataService.decode(control.tag) !== null);
      const paragraphCollections = semanticControls.map((control) => control.getRange("Content").paragraphs);
      paragraphCollections.forEach((paragraphs) => paragraphs.load("items/text"));
      await context.sync();

      return paragraphCollections
        .flatMap((paragraphs) => paragraphs.items.map((paragraph) => paragraph.text.trim()))
        .filter(Boolean);
    });
  }

  async readReferences(): Promise<string[]> {
    return Word.run(async (context) => {
      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();

      const referenceControls = controls.items.filter((control) => {
        const metadata = metadataService.decode(control.tag);
        return metadata?.type === "reference" || metadata?.type === "references";
      });
      const paragraphCollections = referenceControls.map((control) => control.getRange("Content").paragraphs);
      paragraphCollections.forEach((paragraphs) => paragraphs.load("items/text"));
      await context.sync();

      const headingPattern = /^(?:references|bibliography|works?\s+cited|endnotes?|footnotes?)$/i;
      const refs = paragraphCollections.flatMap((paragraphs) =>
        paragraphs.items
          .map((paragraph) => paragraph.text.trim())
          .filter(Boolean)
          .flatMap(splitNumberedReferences)
      );
      const tagged = refs.filter(r => r && !headingPattern.test(r));
      if (tagged.length > 0) return tagged;

      // Fallback: no paragraph is tagged reference/references — most likely
      // Word's own native Citations & Bibliography feature (Insert >
      // Bibliography), whose entries live inside a FIELD-backed content
      // control we never manage to (and shouldn't try to) nest our own
      // content controls into. Pull every non-empty paragraph after the
      // FIRST exact References/Bibliography/Endnotes heading directly, same
      // as readBodyOutline's read-time classification.
      const allParas = context.document.body.paragraphs;
      allParas.load("items/text");
      await context.sync();
      const headingIdx = allParas.items.findIndex((p) => headingPattern.test(p.text.trim()));
      if (headingIdx === -1) return [];
      return allParas.items
        .slice(headingIdx + 1)
        .map((p) => p.text.trim())
        .filter(Boolean)
        .flatMap(splitNumberedReferences)
        .filter((r) => r && !headingPattern.test(r));
    });
  }

  // Read real Word footnotes (Insert → Footnote). For each footnote we capture
  // its text plus the character offset of its in-text reference mark within the
  // owning body paragraph, so the exporter can re-anchor it at exactly the right
  // spot. paraIndex is the index into document.body.paragraphs — the same order
  // readBodyOutline walks, so the exporter can map footnotes to paragraph blocks.
  async readFootnotes(): Promise<Array<{ paraIndex: number; offset: number; text: string }>> {
    return Word.run(async (context) => {
      // Fast path: if the document has no footnotes at all, skip the per-paragraph
      // footnote-collection loads (hundreds of proxy loads on a long doc). These
      // journal articles use superscript-text citations, not real Word footnotes,
      // so this is the common case. body.footnotes is WordApi 1.5+; fall back to
      // the per-paragraph scan if it's unavailable.
      try {
        const docNotes = context.document.body.footnotes;
        docNotes.load("items");
        await context.sync();
        if (docNotes.items.length === 0) return [];
      } catch {
        /* body.footnotes unsupported — fall through to the paragraph scan */
      }

      const paras = context.document.body.paragraphs;
      paras.load("items");
      await context.sync();

      const noteColls = paras.items.map((p) => p.footnotes);
      noteColls.forEach((nc) => nc.load("items"));
      await context.sync();

      const pending: Array<{ paraIndex: number; bodyObj: Word.Body; offsetRange: Word.Range }> = [];
      paras.items.forEach((p, pi) => {
        for (const note of noteColls[pi].items) {
          // chars from paragraph start up to (not including) the reference mark
          const offsetRange = p.getRange(Word.RangeLocation.start).expandTo(note.reference.getRange(Word.RangeLocation.start));
          offsetRange.load("text");
          const bodyObj = note.body;
          bodyObj.load("text");
          pending.push({ paraIndex: pi, bodyObj, offsetRange });
        }
      });
      if (!pending.length) return [];
      await context.sync();

      return pending.map((x) => ({
        paraIndex: x.paraIndex,
        offset: x.offsetRange.text.length,
        text: x.bodyObj.text.replace(/\s+/g, " ").trim()
      }));
    });
  }

  async readFigures(): Promise<Array<{ id: string; caption: string; imageBase64?: string }>> {
    return Word.run(async (context) => {
      const controls = context.document.contentControls;
      controls.load("items/tag,items/text");
      await context.sync();

      const decoded = controls.items
        .map((control) => ({ control, metadata: metadataService.decode(control.tag) }))
        .filter((entry): entry is { control: Word.ContentControl; metadata: SemanticMetadata } => entry.metadata !== null);

      const figures = decoded.filter((entry) => entry.metadata.type === "figure");
      const captions = decoded.filter((entry) => entry.metadata.type === "figure_caption");
      const imageResults = figures.map((entry) => {
        const pictures = entry.control.getRange("Content").inlinePictures;
        pictures.load("items");
        const html = entry.control.getHtml();
        return { entry, pictures, html };
      });
      // A real Word footnote referenced inside a caption (e.g. "...police²¹")
      // leaves an unprintable footnote-reference placeholder char (U+0002) in
      // ContentControl.text — with nothing to convert it, it renders as a
      // tofu box in the PDF and the footnote's own text is silently dropped.
      // Splice each caption's own footnotes back in as `<fn>` tags, the same
      // mechanism body paragraphs use, so it renders as a real superscript +
      // page-bottom footnote instead.
      const captionNoteColls = captions.map((entry) => entry.control.getRange("Content").footnotes);
      captionNoteColls.forEach((nc) => nc.load("items"));
      await context.sync();

      const base64Results = imageResults.map(({ pictures }) =>
        pictures.items.length > 0 ? pictures.items[0].getBase64ImageSrc() : null
      );
      const captionNotePending: Array<{ capIdx: number; offsetRange: Word.Range; bodyObj: Word.Body }> = [];
      captions.forEach((entry, capIdx) => {
        const contentStart = entry.control.getRange("Content").getRange(Word.RangeLocation.start);
        for (const note of captionNoteColls[capIdx].items) {
          const offsetRange = contentStart.expandTo(note.reference.getRange(Word.RangeLocation.start));
          offsetRange.load("text");
          const bodyObj = note.body;
          bodyObj.load("text");
          captionNotePending.push({ capIdx, offsetRange, bodyObj });
        }
      });
      await context.sync();

      const captionNotesByIdx = new Map<number, Array<{ offset: number; text: string }>>();
      for (const p of captionNotePending) {
        const arr = captionNotesByIdx.get(p.capIdx) ?? [];
        arr.push({ offset: p.offsetRange.text.length, text: p.bodyObj.text.replace(/\s+/g, " ").trim() });
        captionNotesByIdx.set(p.capIdx, arr);
      }

      const htmlImageBase64 = (html: string): string | undefined => {
        const match = html.match(/src=["']data:image\/[^;]+;base64,([^"']+)["']/i);
        return match?.[1];
      };

      const captionTextFor = (capIdx: number): string => {
        const raw = captions[capIdx].control.text.trim();
        const strip = (s: string) => s.replace(/\x1E/g, "-").replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");
        const notes = captionNotesByIdx.get(capIdx);
        if (!notes || notes.length === 0) return strip(raw);
        const sorted = [...notes].sort((a, b) => a.offset - b.offset);
        let result = "";
        let cursor = 0;
        for (const note of sorted) {
          const off = Math.max(cursor, Math.min(note.offset, raw.length));
          result += strip(raw.slice(cursor, off));
          result += `<fn>${note.text}</fn>`;
          cursor = off;
        }
        result += strip(raw.slice(cursor));
        return result;
      };

      return imageResults.map(({ entry }, index) => {
        const capIdx = captions.findIndex((caption) => caption.metadata.parent === entry.metadata.id);
        return {
          id: entry.metadata.id ?? `fig${index + 1}`,
          caption: capIdx >= 0 ? captionTextFor(capIdx) : "",
          imageBase64: base64Results[index]?.value || htmlImageBase64(imageResults[index].html.value) || undefined
        };
      });
    });
  }

  async readTables(): Promise<Array<{ index: number; rows: string[][]; isUniform: boolean; nestingLevel: number }>> {
    return Word.run(async (context) => {
      const tables = context.document.body.tables;
      tables.load("items/values,isUniform,nestingLevel");
      await context.sync();
      const results: Array<{ index: number; rows: string[][]; isUniform: boolean; nestingLevel: number }> = [];
      for (let tableIndex = 0; tableIndex < tables.items.length; tableIndex += 1) {
        const table = tables.items[tableIndex];
        results.push({
          index: tableIndex + 1,
          rows: table.values.map((row) => row.map((cell) => cell.trim())),
          isUniform: table.isUniform,
          nestingLevel: table.nestingLevel
        });
      }
      return results;
    });
  }

  async focusValidationIssue(issue: ValidationIssue): Promise<void> {
    await Word.run(async (context) => {
      const metadata = issue.metadata ?? {};
      const targetId = typeof metadata.targetId === "string" ? metadata.targetId : undefined;
      const text = typeof metadata.text === "string" ? metadata.text : undefined;
      const caption = typeof metadata.caption === "string" ? metadata.caption : undefined;
      const tableIndex =
        typeof metadata.tableIndex === "number"
          ? metadata.tableIndex
          : typeof issue.location === "string"
            ? Number(issue.location.match(/^Table (\d+)$/)?.[1])
            : NaN;

      if (targetId || caption) {
        const controls = context.document.contentControls;
        controls.load("items/tag,text");
        await context.sync();
        const match = controls.items.find((control) => {
          const decoded = metadataService.decode(control.tag);
          return (targetId && decoded?.id === targetId) || (caption && control.text.trim() === caption.trim());
        });
        if (match) {
          match.getRange("Content").select("Select");
          await context.sync();
          return;
        }
      }

      if (text) {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text");
        await context.sync();
        const match = paragraphs.items.find((paragraph) => paragraph.text.trim() === text.trim());
        if (match) {
          match.getRange("Whole").select("Select");
          await context.sync();
          return;
        }
      }

      if (Number.isFinite(tableIndex) && tableIndex > 0) {
        const tables = context.document.body.tables;
        tables.load("items");
        await context.sync();
        const table = tables.items[tableIndex - 1];
        if (table) {
          table.rows.getFirst().cells.getFirst().body.getRange("Whole").select("Select");
          await context.sync();
          return;
        }
      }

      context.document.body.getRange("Start").select("Select");
      await context.sync();
    });
  }

  async insertText(text: string): Promise<void> {
    await Word.run(async (context) => {
      context.document.getSelection().insertText(text, Word.InsertLocation.replace);
      await context.sync();
    });
  }

  async generateTableOfContents(): Promise<void> {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      context.document.tablesOfContents.add(selection, {
        useBuiltInHeadingStyles: true,
        upperHeadingLevel: 1,
        lowerHeadingLevel: 6,
        includePageNumbers: true,
        rightAlignPageNumbers: true,
        hidePageNumbersOnWeb: true
      });
      await context.sync();
    });
  }

  async removeDecorativeLines(): Promise<number> {
    return Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items/text");
      await context.sync();
      let removed = 0;

      for (const paragraph of paragraphs.items) {
        const text = paragraph.text.trim();
        if (/^[\s_\-=—–]{3,}$/.test(text)) {
          paragraph.delete();
          removed += 1;
          continue;
        }

        try {
          const borders = paragraph.borders;
          borders.load("items");
          await context.sync();
          for (const border of borders.items) {
            border.isVisible = false;
            border.lineStyle = "None";
          }
        } catch {
          // Paragraph border APIs are desktop-host only; line-only paragraphs still get removed cross-host.
        }
      }

      await context.sync();
      return removed;
    });
  }

  async updateAllStyles(): Promise<void> {
    await Word.run(async (context) => {
      // Remember where the user is so we can snap the viewport back at the end
      // instead of letting Word scroll-follow the formatting pass.
      const savedSelection = context.document.getSelection();
      savedSelection.track();

      this.applyDocumentDefaults(context);
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      for (const paragraph of paragraphs.items) {
        paragraph.font.name = bodyFont();
        paragraph.font.size = 11;
        paragraph.font.bold = false;
        paragraph.font.italic = false;
        paragraph.font.color = "black";
        paragraph.alignment = "Justified";
        paragraph.spaceAfter = 6;
        paragraph.spaceBefore = 0;
        // On Mac Word, Office.js `lineSpacing` sets a MULTIPLE rule of value/12,
        // NOT points. 18 -> Multiple 1.5; 12 -> Multiple 1.0 (single). Body = 1.5.
        paragraph.lineSpacing = ONE_HALF_LINE;
      }
      await this.reapplySemanticTypography(context);
      await this.applyReferenceNumbering(context);
      await this.applyTableFormatting(context);
      // NOTE: figure/table numbering (numberObjects) is intentionally NOT run
      // here — it mutates the document (inserts caption paragraphs) and must only
      // happen on the explicit "Auto Number Figures/Tables" buttons.

      // Restore the original cursor/viewport, then untrack the proxy.
      savedSelection.select();
      await context.sync();
      savedSelection.untrack();
      await context.sync();
    });
  }

  // References body -> arabic numbered list, 10pt. One content control, so the
  // extra syncs here are bounded (not per-paragraph).
  private async applyReferenceNumbering(context: Word.RequestContext): Promise<void> {
    const controls = context.document.contentControls;
    controls.load("items/tag");
    await context.sync();
    for (const control of controls.items) {
      const metadata = metadataService.decode(control.tag);
      if (!metadata || (metadata.type !== "references" && metadata.type !== "reference")) continue;
      const paragraphs = control.getRange("Content").paragraphs;
      paragraphs.load("items");
      await context.sync();
      if (paragraphs.items.length === 0) continue;

      // Strip whatever numbering/bullets the author used ("1." "[1]" "a)" "•" ...)
      // so our clean arabic list is the only numbering.
      paragraphs.items.forEach((p) => p.load("text"));
      await context.sync();
      for (const p of paragraphs.items) {
        const stripped = stripLeadingMarker(p.text);
        if (stripped && stripped !== p.text) {
          p.getRange("Content").insertText(stripped, Word.InsertLocation.replace);
        }
      }
      await context.sync();

      const list = paragraphs.items[0].startNewList();
      list.load("id");
      await context.sync();
      list.setLevelNumbering(0, Word.ListNumbering.arabic);
      paragraphs.items[0].font.size = 10;
      for (let i = 1; i < paragraphs.items.length; i++) {
        paragraphs.items[i].attachToList(list.id, 0);
        paragraphs.items[i].font.size = 10;
      }
    }
  }

  // Tables: center on page, auto-fit to margins, 10pt cells centered, header
  // row bold 11pt. Batched so the whole document's tables cost ~3 syncs total.
  private async applyTableFormatting(context: Word.RequestContext): Promise<void> {
    const tables = context.document.body.tables;
    tables.load("items");
    await context.sync();
    if (tables.items.length === 0) return;

    const cellParagraphs = tables.items.map((table) => {
      table.rows.load("items");
      const paragraphs = table.getRange().paragraphs;
      paragraphs.load("items");
      return paragraphs;
    });
    await context.sync();

    tables.items.forEach((table, i) => {
      table.horizontalAlignment = "Centered";
      table.font.name = bodyFont();
      table.font.size = 10;
      table.autoFitWindow();
      for (const paragraph of cellParagraphs[i].items) {
        paragraph.alignment = "Centered";
      }
      if (table.rows.items.length > 0) {
        const header = table.rows.items[0];
        header.font.bold = true;
        header.font.size = 11;
      }
    });
  }

  // Sequentially label figures/tables ("Figure 1", "Table 2", ...) in document
  // order. Each label lives in the object's caption (matched by metadata.parent).
  // If an object has no caption, a placeholder caption is inserted below it.
  // Idempotent: re-running relabels existing captions, never duplicates.
  async numberFiguresAndTables(): Promise<{ figures: number; tables: number }> {
    return Word.run(async (context) => {
      const figures = await this.numberObjects(context, "figure", "figure_caption", "Figure");
      const tables = await this.numberObjects(context, "table", "table_caption", "Table");
      await context.sync();
      return { figures, tables };
    });
  }

  // Set (or create) the caption for the figure/table the cursor is currently in.
  // Covers the "object has no caption yet" case: type a caption and it's inserted
  // below the object, tagged and linked. If a caption already exists, it's updated.
  async setCaptionForSelection(text: string): Promise<void> {
    const caption = text.trim();
    if (!caption) throw new Error("Type a caption first.");
    await Word.run(async (context) => {
      const parent = context.document.getSelection().parentContentControlOrNullObject;
      parent.load("isNullObject,tag");
      await context.sync();
      if (parent.isNullObject) throw new Error("Click inside a figure or table first.");
      const metadata = metadataService.decode(parent.tag);
      if (!metadata || (metadata.type !== "figure" && metadata.type !== "table")) {
        throw new Error("Selection is not a figure or table.");
      }
      const captionType: SemanticType = metadata.type === "figure" ? "figure_caption" : "table_caption";

      const controls = context.document.contentControls;
      controls.load("items/tag");
      await context.sync();
      const existing = controls.items.find((control) => {
        const meta = metadataService.decode(control.tag);
        return meta?.type === captionType && meta.parent === metadata.id;
      });

      if (existing) {
        existing.getRange("Content").insertText(caption, Word.InsertLocation.replace);
      } else {
        const captionMeta = metadataService.create(captionType, { parent: metadata.id });
        const captionParagraph = parent.getRange("After").insertParagraph(caption, Word.InsertLocation.after);
        applyTypographyToRange(captionParagraph.getRange(), typographyByType.caption);
        captionParagraph.alignment = "Centered";
        const captionControl = captionParagraph.getRange().insertContentControl();
        captionControl.title = this.titleForMetadata(captionMeta);
        captionControl.tag = metadataService.encode(captionMeta);
        captionControl.appearance = Word.ContentControlAppearance.boundingBox;
        await metadataService.upsertDocumentIndex(context, captionMeta);
      }
      await context.sync();
    });
  }

  private async numberObjects(
    context: Word.RequestContext,
    type: SemanticType,
    captionType: SemanticType,
    label: "Figure" | "Table"
  ): Promise<number> {
    const controls = context.document.contentControls;
    controls.load("items/tag,items/text");
    await context.sync();

    const decoded = controls.items
      .map((control) => ({ control, metadata: metadataService.decode(control.tag) }))
      .filter((entry): entry is { control: Word.ContentControl; metadata: SemanticMetadata } => entry.metadata !== null);
    const objects = decoded.filter((entry) => entry.metadata.type === type);
    const captions = decoded.filter((entry) => entry.metadata.type === captionType);
    const prefix = new RegExp(`^\\s*${label}\\s+\\d+\\s*[:.\\-]?\\s*`, "i");

    let count = 0;
    for (const { control: object, metadata } of objects) {
      count += 1;
      const existing = captions.find((entry) => entry.metadata.parent === metadata.id);
      if (existing) {
        const text = existing.control.text.replace(prefix, "").trim();
        existing.control.getRange("Content").insertText(`${label} ${count}${text ? `: ${text}` : ""}`, Word.InsertLocation.replace);
      } else {
        const captionMeta = metadataService.create(captionType, { parent: metadata.id });
        const captionParagraph = object.getRange("After").insertParagraph(`${label} ${count}`, Word.InsertLocation.after);
        applyTypographyToRange(captionParagraph.getRange(), typographyByType.caption);
        captionParagraph.alignment = "Centered";
        const captionControl = captionParagraph.getRange().insertContentControl();
        captionControl.title = this.titleForMetadata(captionMeta);
        captionControl.tag = metadataService.encode(captionMeta);
        captionControl.appearance = Word.ContentControlAppearance.boundingBox;
        await metadataService.upsertDocumentIndex(context, captionMeta);
      }
    }
    await context.sync();
    return count;
  }

  private titleForMetadata(metadata: SemanticMetadata): string {
    if (metadata.type === "section" && metadata.level) return `Libertatem Section Level ${metadata.level}`;
    return `Libertatem ${metadata.type.replaceAll("_", " ")}`;
  }

  private applyDocumentDefaults(context: Word.RequestContext): void {
    // Only set the default font here. Do NOT force whole-body single spacing —
    // that was overriding the per-paragraph 1.5-line rule set later.
    context.document.body.font.name = bodyFont();
  }

  private async reapplySemanticTypography(context: Word.RequestContext): Promise<void> {
    const controls = context.document.contentControls;
    controls.load("items/tag,items/text");
    await context.sync();

    // Batch all controls into two syncs total. The previous version awaited a
    // sync per control, which made Word render and scroll to each range one by
    // one — the wild up/down jumping the user saw.
    const jobs: { paragraphs: Word.ParagraphCollection; spec: TypographySpec; type: SemanticType }[] = [];
    for (const control of controls.items) {
      const metadata = metadataService.decode(control.tag);
      if (!metadata) continue;
      const spec = this.specForControl(metadata, control.text);
      const range = control.getRange("Content");
      applyTypographyToRange(range, spec);
      const paragraphs = range.paragraphs;
      paragraphs.load("items");
      jobs.push({ paragraphs, spec, type: metadata.type });
    }
    await context.sync();

    for (const { paragraphs, spec, type } of jobs) {
      paragraphs.items.forEach((paragraph, index) => {
        paragraph.font.name = bodyFont();
        paragraph.font.size = spec.fontSize;
        if (type === "author") {
          // Author block is multi-paragraph: name (para 0) bold, affiliation /
          // ORCID lines italic. The whole-body normalize pass flattened these,
          // so re-assert by position here.
          paragraph.font.bold = index === 0;
          paragraph.font.italic = index !== 0;
        } else if (!spec.preserveRuns) {
          paragraph.font.bold = spec.bold ?? false;
          paragraph.font.italic = spec.italic ?? false;
        }
        paragraph.font.color = "black";
        paragraph.alignment = spec.alignment ?? "Left";
        paragraph.spaceAfter = spec.spaceAfter ?? 0;
        paragraph.spaceBefore = spec.spaceBefore ?? 0;
        // Mac Word: `lineSpacing` is a Multiple rule of value/12, not points.
        // 18 -> Multiple 1.5; 12 -> Multiple 1.0 (single). Using the property
        // (not space1/space1Pt5) reliably downgrades title/subtitle to single.
        paragraph.lineSpacing = spec.lineSpacing === "onePointFive" ? ONE_HALF_LINE : SINGLE_LINE;
      });
    }
  }

  // The Abstract/Keywords/References heading controls are tagged as a generic
  // section (level 2) by the insert flow, so specForMetadata would size them
  // like a subsection (13pt). Detect them by their heading text and apply the
  // dedicated 14pt heading spec instead.
  private specForControl(metadata: SemanticMetadata, text: string): TypographySpec {
    if (metadata.type === "section" && metadata.level === 2) {
      const label = text.trim().toLowerCase();
      if (label === "abstract") return typographyByType.abstract_heading;
      if (label === "keywords") return typographyByType.keywords_heading;
      if (label === "references") return typographyByType.references_heading;
    }
    return specForMetadata(metadata);
  }
}

function extractInlineRichText(html: string, plainText: string): string | undefined {
  if (!html) return undefined;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return undefined;
  }
  const body = doc.body;
  if (!body) return undefined;
  const rich = Array.from(body.childNodes).map((node) => sanitizeInlineNode(node)).join("").trim();
  if (!rich) return undefined;
  const plain = stripInlineTags(rich).replace(/\s+/g, " ").trim();
  if (plain === plainText.replace(/\s+/g, " ").trim() && rich === escapeHtml(plainText).trim()) return undefined;
  return rich;
}

type RichParagraph = {
  plain: string;
  rich: string;
  normalizedPlain: string;
  hasRich: boolean;
};

function normalizeParagraphText(value: string): string {
  return value.replace(/\x1E/g, "-").replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "").replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

// Comparison key for pairing an Office.js paragraph with its OOXML twin. Office.js
// flattens a native equation into separators/control marks and math-italic
// letters while the OOXML has plain <m:t> text, so exact-text equality never
// matches an equation paragraph. Letters/digits only (NFKC folds math-italic
// letters back to ASCII) is identical for ordinary text and tolerant for math.
function paragraphKey(value: string): string {
  return value.normalize("NFKC").replace(/[^\p{L}\p{N}]+/gu, "").toLowerCase();
}

function parseOoxmlDocument(xml: string): Document | null {
  if (!xml) return null;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, "application/xml");
  } catch {
    return null;
  }
  return doc.getElementsByTagName("parsererror").length > 0 ? null : doc;
}

// Only a DISPLAY/standalone equation counts, not one sitting inline inside a
// sentence (e.g. "Distillation loss Ldistill aligns...") — an inline one must
// stay tagged "paragraph" so the sentence around it isn't swallowed into the
// equation block. <m:oMathPara> (Word's own display-equation wrapper) is NOT
// a reliable signal here — it doesn't survive Word's own body.getOoxml()
// export in testing, even though it's present in the raw .docx XML. Instead:
// strip every <m:oMath> zone out of the paragraph and see what real text is
// left. A standalone equation paragraph has nothing left (at most a trailing
// equation number like "(1)"); an inline one still has its surrounding
// sentence.
function paragraphHasEquation(p: Element): boolean {
  const hasMath = Array.from(p.getElementsByTagName("*")).some((el) => el.localName === "oMath");
  if (!hasMath) return false;

  const nonMathText = Array.from(p.childNodes).map((node) => textOutsideMath(node)).join("").trim();
  return nonMathText === "" || /^[(\d.)\s]+$/.test(nonMathText);
}

function textOutsideMath(node: Node): string {
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as Element;
  if (el.localName === "oMath") return "";
  if (el.localName === "r") return readOoxmlRunText(el);
  return Array.from(el.childNodes).map(textOutsideMath).join("");
}

// Nearest enclosing <w:sdt> for a paragraph, if any — used to tell whether a
// paragraph containing an equation is already inside one of OUR content
// controls (and if so, decode what it's tagged as) or genuinely untagged.
function elementChildren(el: Element): Element[] {
  return Array.from(el.childNodes).filter((n): n is Element => n.nodeType === Node.ELEMENT_NODE);
}

function enclosingSdtTag(p: Element): string | undefined {
  let node: Node | null = p.parentNode;
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).localName === "sdt") {
      const sdt = node as Element;
      const sdtPr = elementChildren(sdt).find((el) => el.localName === "sdtPr");
      const tagEl = sdtPr && elementChildren(sdtPr).find((el) => el.localName === "tag");
      return tagEl?.getAttribute("w:val") ?? undefined;
    }
    node = node.parentNode;
  }
  return undefined;
}

function bodyParagraphElements(doc: Document): Element[] {
  const body = Array.from(doc.getElementsByTagName("*")).find((el) => el.localName === "body");
  return Array.from((body ?? doc).getElementsByTagName("*")).filter((el) => el.localName === "p");
}

// Returns the set of semantic ids for content controls tagged type
// "paragraph" whose content contains a native Word equation object
// (<m:oMath>/<m:oMathPara>) — read directly from each control's own
// <w:tag> value, so there's no ambiguity matching it back to a Word.Paragraph.
function findEquationTaggedAsParagraph(xml: string): Set<string> {
  const ids = new Set<string>();
  const doc = parseOoxmlDocument(xml);
  if (!doc) return ids;

  for (const p of bodyParagraphElements(doc)) {
    if (!paragraphHasEquation(p)) continue;
    const meta = metadataService.decode(enclosingSdtTag(p));
    if (meta?.type === "paragraph" && meta.id) ids.add(meta.id);
  }
  return ids;
}

// The reverse mistake: a paragraph tagged "equation" by an Auto-Tag run that
// predates the oMathPara-only rule (it used to fire on any bare <m:oMath>,
// including an equation sitting inline inside a sentence) — revert those back
// to "paragraph" so the surrounding sentence isn't stuck permanently
// mis-scoped as an equation block.
function findParagraphTaggedAsEquation(xml: string): Set<string> {
  const ids = new Set<string>();
  const doc = parseOoxmlDocument(xml);
  if (!doc) return ids;

  for (const p of bodyParagraphElements(doc)) {
    const meta = metadataService.decode(enclosingSdtTag(p));
    if (meta?.type !== "equation" || !meta.id) continue;
    if (!paragraphHasEquation(p)) ids.add(meta.id);
  }
  return ids;
}

// Normalized plain text of every paragraph that contains a native equation
// object but is NOT inside one of our own content controls (i.e. would read
// as an untagged paragraph) — a small, low-collision-risk multiset to check
// candidate untagged paragraphs against, without calling getOoxml() per
// paragraph (slow, and unreliable on a large real document).
function untaggedEquationTextCounts(xml: string): Map<string, number> {
  const counts = new Map<string, number>();
  const doc = parseOoxmlDocument(xml);
  if (!doc) return counts;

  for (const p of bodyParagraphElements(doc)) {
    if (!paragraphHasEquation(p)) continue;
    if (metadataService.decode(enclosingSdtTag(p))) continue; // already one of ours
    const text = paragraphKey(readOoxmlNodeText(p));
    if (text) counts.set(text, (counts.get(text) ?? 0) + 1);
  }
  return counts;
}

function parseRichParagraphsFromOoxml(xml: string): RichParagraph[] {
  if (!xml) return [];
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, "application/xml");
  } catch {
    return [];
  }
  if (doc.getElementsByTagName("parsererror").length > 0) return [];

  const rels = readRelationshipTargets(doc);
  // Only parse paragraphs from <w:body>, not from footnotes/endnotes/headers/footers
  const body = Array.from(doc.getElementsByTagName("*")).find((el) => el.localName === "body");
  const pElements = body
    ? Array.from(body.getElementsByTagName("*")).filter((el) => el.localName === "p")
    : Array.from(doc.getElementsByTagName("*")).filter((el) => el.localName === "p");
  return pElements
    .map((paragraph) => {
      const plain = readOoxmlNodeText(paragraph);
      const rich = Array.from(paragraph.childNodes).map((node) => sanitizeOoxmlNode(node, rels)).join("");
      return {
        plain,
        rich,
        normalizedPlain: paragraphKey(plain),
        hasRich: rich !== escapeHtml(plain)
      };
    });
}

function alignRichParagraphs(paragraphs: string[], richParagraphs: RichParagraph[]): Array<string | undefined> {
  const aligned: Array<string | undefined> = [];
  let cursor = 0;
  // A paragraph that finds no twin within this window is left un-enriched and
  // does NOT move the cursor — otherwise one unmatched paragraph would scan to
  // the end of the document and strip formatting from everything after it.
  const WINDOW = 12;

  for (const paragraph of paragraphs) {
    const key = paragraphKey(paragraph);
    let richText: string | undefined;
    const end = Math.min(richParagraphs.length, cursor + WINDOW);
    for (let i = cursor; i < end; i++) {
      const candidate = richParagraphs[i];
      if (!candidate || candidate.normalizedPlain !== key) continue;
      if (candidate.hasRich) richText = candidate.rich.trim();
      cursor = i + 1;
      break;
    }
    aligned.push(richText);
  }

  return aligned;
}

function readRelationshipTargets(doc: Document): Map<string, string> {
  const targets = new Map<string, string>();
  Array.from(doc.getElementsByTagName("*")).forEach((el) => {
    if (el.localName !== "Relationship") return;
    const id = el.getAttribute("Id");
    const target = el.getAttribute("Target");
    if (id && target) targets.set(id, target);
  });
  return targets;
}

function sanitizeOoxmlNode(node: Node, rels: Map<string, string>): string {
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as Element;
  // Native Word equation: rebuild sub/superscripts, fractions, accents… from the
  // OMML (Office.js' flattened text drops them all).
  if (el.localName === "oMath") return ommlToHtml(el);
  if (el.localName === "r") return sanitizeOoxmlRun(el);
  if (el.localName === "hyperlink") {
    const id =
      el.getAttribute("r:id") ||
      el.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id");
    const href = id ? rels.get(id) : "";
    const inner = Array.from(el.childNodes).map((child) => sanitizeOoxmlNode(child, rels)).join("");
    return href && /^https?:\/\//i.test(href) ? `<a href="${escapeAttribute(href)}">${inner}</a>` : inner;
  }
  return Array.from(el.childNodes).map((child) => sanitizeOoxmlNode(child, rels)).join("");
}

function sanitizeOoxmlRun(run: Element): string {
  // Footnote/endnote reference: extract id and emit as <sup>N</sup>.
  // These runs have no w:t — readOoxmlRunText returns "" for them, so
  // handle before the empty-text bail-out.
  const fnRef = Array.from(run.childNodes).find((node) =>
    node.nodeType === Node.ELEMENT_NODE &&
    ((node as Element).localName === "footnoteReference" || (node as Element).localName === "endnoteReference")
  ) as Element | undefined;
  if (fnRef) {
    const refId = fnRef.getAttribute("w:id") || fnRef.getAttribute("id") || "";
    return refId ? `<sup>${escapeHtml(refId)}</sup>` : "";
  }

  const text = readOoxmlRunText(run);
  if (!text) return "";
  const rpr = Array.from(run.childNodes).find((node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).localName === "rPr") as Element | undefined;
  let output = escapeHtml(text);

  if (!rpr) return output;

  const has = (name: string) => Array.from(rpr.childNodes).some((node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).localName === name);
  const vert = Array.from(rpr.childNodes).find((node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).localName === "vertAlign") as Element | undefined;
  const vertVal = vert ? vert.getAttribute("w:val") || vert.getAttribute("val") || "" : "";

  if (vertVal === "superscript") output = `<sup>${output}</sup>`;
  if (vertVal === "subscript") output = `<sub>${output}</sub>`;
  if (has("u")) output = `<u>${output}</u>`;
  if (has("i") || has("iCs")) output = `<em>${output}</em>`;
  if (has("b") || has("bCs")) output = `<strong>${output}</strong>`;
  return output;
}

function readOoxmlRunText(run: Element): string {
  let text = "";
  Array.from(run.childNodes).forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el.localName === "t") text += el.textContent || "";
    else if (el.localName === "tab") text += "\t";
    else if (el.localName === "br" || el.localName === "cr") text += "\n";
    else if (el.localName === "noBreakHyphen") text += "-";
    else if (el.localName === "footnoteReference" || el.localName === "endnoteReference") {
      // Skip — don't include in plain text (used for alignment with paragraph.text
      // which has these as control chars that get stripped). The rich text path in
      // sanitizeOoxmlRun handles these separately with <sup> wrapping.
    }
  });
  return text;
}

function readOoxmlNodeText(node: Node): string {
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as Element;
  if (el.localName === "r") return readOoxmlRunText(el);
  return Array.from(el.childNodes).map(readOoxmlNodeText).join("");
}

function sanitizeInlineNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent || "");
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === "br") return "<br>";
  const inner = Array.from(el.childNodes).map((child) => sanitizeInlineNode(child)).join("");
  if (tag === "p" || tag === "div" || tag === "body" || tag === "html") return inner;
  if (tag === "strong" || tag === "b") return `<strong>${inner}</strong>`;
  if (tag === "em" || tag === "i") return `<em>${inner}</em>`;
  if (tag === "u") return `<u>${inner}</u>`;
  if (tag === "sup") return `<sup>${inner}</sup>`;
  if (tag === "sub") return `<sub>${inner}</sub>`;
  if (tag === "a") {
    const href = el.getAttribute("href");
    return href ? `<a href="${escapeAttribute(href)}">${inner}</a>` : inner;
  }
  if (tag === "span") return applySpanFormatting(el, inner);
  return inner;
}

function applySpanFormatting(el: HTMLElement, inner: string): string {
  let output = inner;
  const style = (el.getAttribute("style") || "").toLowerCase();
  if (/vertical-align\s*:\s*super/.test(style)) output = `<sup>${output}</sup>`;
  if (/vertical-align\s*:\s*sub/.test(style)) output = `<sub>${output}</sub>`;
  if (/text-decoration(?:-line)?\s*:\s*[^;]*underline/.test(style)) output = `<u>${output}</u>`;
  if (/font-style\s*:\s*italic/.test(style) || /\bitalic\b/.test(el.className)) output = `<em>${output}</em>`;
  if (/font-weight\s*:\s*(bold|[6-9]00)\b/.test(style)) output = `<strong>${output}</strong>`;
  return output;
}

function stripInlineTags(value: string): string {
  return value.replace(/<[^>]+>/g, "");
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

export const wordService = new WordService();
